import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
// EXRLoader niet meer geïmporteerd (2026-10-12): de HDRI staat uit, en de import kostte twee downloads
// (EXRLoader + fflate). Zet je de HDRI terug, dan deze import ook.
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import Lenis from 'lenis';

// TIJDELIJK (2026-10-12): met ?timing=1 staat linksboven per stap hoe lang het laden duurde,
// om de trage voorpagina op Vince z'n iPhone te ontleden. Weghalen na de meting.
const TIMING = /[?&]timing=1\b/.test(location.search);
const _t = {};
function tmark(name) {
  if (!TIMING) return;
  _t[name] = performance.now();
  let el = document.getElementById('hp-timing');
  if (!el) {
    el = document.createElement('div');
    el.id = 'hp-timing';
    el.style.cssText = 'position:fixed;left:8px;top:8px;z-index:2147483000;background:#99BEA7;color:#1B2A1F;font:600 12px/1.4 monospace;padding:6px 8px;white-space:pre;pointer-events:none';
    document.body.appendChild(el);
  }
  const sec = (ms) => (ms / 1000).toFixed(1);
  const d = (a, b) => (_t[a] != null && _t[b] != null) ? sec(_t[b] - _t[a]) : '…';
  const glb = performance.getEntriesByType('resource').find(r => /\.glb/.test(r.name));
  el.textContent =
    'scripts klaar  ' + (_t.module != null ? sec(_t.module) : '…') + 's\n' +
    'model laden    ' + d('glbStart', 'glbLoaded') + (glb ? '  (download ' + sec(glb.responseEnd - glb.startTime) + ')' : '') + '\n' +
    'model bouwen   ' + d('glbLoaded', 'warmStart') + '\n' +
    'shaders        ' + d('warmStart', 'warmEnd') + '\n' +
    'spoel zichtbaar ' + (_t.reveal != null ? sec(_t.reveal) + 's' : '…');
}
tmark('module');

const canvas = document.querySelector('#scene');
const statsEl = document.querySelector('#stats');
const loaderEl = document.querySelector('#loader');
const loaderPercentEl = document.querySelector('#loader-percent');
const loaderFillEl = document.querySelector('#loader-fill');
let introStart = null; // performance.now() bij hideLoader; drijft de intro-reveal

// Mouse parallax: cursor-position genormaliseerd naar -1..1 op beide assen.
// Smooth-lerp variants worden in animate() per frame bijgewerkt.
let mouseTargetX = 0;
let mouseTargetY = 0;
let mouseSmoothX = 0;
let mouseSmoothY = 0;
// Alleen een echte muis (2026-11-03). Een tik stuurt ook een nagebootste muisbeweging, en
// daarvan draaide de spoel op een telefoon ~2 s naar de tikplek bij; zo lang bleef de scène
// tekenen, net als er een volgende tik kwam. Zie "Stilstaan op telefoons" boven animate().
let lastTouchAt = -1e9;
window.addEventListener('touchstart', () => { lastTouchAt = performance.now(); }, { capture: true, passive: true });
window.addEventListener('mousemove', (e) => {
  if (performance.now() - lastTouchAt < 1000) return;
  mouseTargetX = (e.clientX / window.innerWidth - 0.5) * 2;
  mouseTargetY = (e.clientY / window.innerHeight - 0.5) * 2;
}, { passive: true });

// Native scroll met Lenis smooth-scroll (lerp 0.18): geen section-snap meer
// (companion-redesign inc 1). De 3D-journey (Hero → Engineering → Research →
// Catalogus) speelt scroll-gekoppeld mee zonder de scroll te onderscheppen of te
// gaten — je scrollt overal vrij, ook door catalogus, about en footer. Lenis
// schrijft de echte native scroll-positie, dus alle bestaande 'scroll'-listeners
// (updateScroll, updateColorTokens, scroll-rail) blijven gewoon werken.
// syncTouch bewust NIET aangezet: leek een goede fix voor de mobile-glow-skip
// (native touch-scroll is ongesmooth, dus een snelle flick kon over het glow-venster
// heenspringen), maar Vince: scrollen ging daarna "100km/h, veeeel te hard" — Lenis'
// eigen touch-virtualisatie/momentum is zonder verdere tuning (syncTouchLerp,
// touchInertiaMultiplier) veel wilder dan native touch-scroll. De glow-skip wordt nu
// opgelost door het venster zelf te verplaatsen (zie DR_START/DR_END), niet door
// touch-scroll physics aan te raken.
//
// Eigen scrollvlak op een touch-telefoon (html.own-scroll, zie layout/theme.liquid en
// homepage.css): daar scrolt #page-scroll en niet window. Alles wat de scroll leest of erop
// luistert gaat via scrollEl/getScrollY/scrollMax hieronder, zodat beide standen dezelfde
// code delen.
const OWN_SCROLL = document.documentElement.classList.contains('own-scroll');
const scrollEl = OWN_SCROLL ? document.getElementById('page-scroll') : null;
const scrollTarget = scrollEl || window;          // waar het 'scroll'-event vuurt
function getScrollY() {
  return scrollEl ? scrollEl.scrollTop : window.scrollY;
}
function scrollMax() {
  return scrollEl
    ? scrollEl.scrollHeight - scrollEl.clientHeight
    : document.documentElement.scrollHeight - window.innerHeight;
}
const lenis = new Lenis({
  lerp: 0.18,           // animatie: lichte snappy smoothing (catalogus wisselt naar 1.0 native via updateScroll)
  wheelMultiplier: 1.0, // normale wheel-gevoeligheid
  touchMultiplier: 1.2,
  smoothWheel: true,
  ...(scrollEl ? { wrapper: scrollEl, content: document.getElementById('page-scroll-content') } : {}),
});
// absTop hergebruikt door de Browse-CTA verderop. In het eigen scrollvlak staat dat vlak
// op top 0, dus rect.top + scrollTop is ook daar de positie in de pagina.
function absTop(el) {
  return el.getBoundingClientRect().top + getScrollY();
}
let ringGroup = null;   // grote 3D-ringen rond de spoel (orbital depth)
let ringMatRefs = null; // [core, halo] ring-materialen, voor fade in de laatste stap

function setLoaderProgress(pct) {
  if (!loaderEl) return; // laadscherm is weg sinds de voorpagina de inhoud eerst toont
  loaderPercentEl.textContent = `${pct}%`;
  loaderFillEl.style.width = `${pct}%`;
}

function setLoaderIndeterminate(label) {
  if (!loaderEl) return;
  loaderPercentEl.textContent = label;
  loaderFillEl.style.width = '100%';
}

function hideLoader() {
  tmark('reveal');
  introStart = performance.now(); // start de intro-reveal zodra het model klaar is
  // Scène vrijgeven: het effen vlak dat de canvas tijdens de pre-warm afdekte (#scene-cover)
  // fadet weg. De canvas zelf bleef al die tijd zichtbaar, zie #scene-cover in de CSS.
  canvas.classList.add('ready'); // statusvlag (opname-modus, metingen)
  const cover = document.getElementById('scene-cover');
  if (cover) { cover.classList.add('gone'); setTimeout(() => cover.remove(), 700); }
  if (!loaderEl) return;
  loaderEl.classList.add('hidden');
  setTimeout(() => loaderEl.remove(), 260); // matcht de kortere 200ms fade
}

// Mobile-init: zwakkere GPU + smaller scherm → lagere pixelRatio en minder MSAA
// (init-time; een oriëntatie-flip vraagt een refresh voor de zwaarste settings,
// de framing zelf reageert wél live op portrait via animate()).
const MOBILE_INIT = window.innerWidth <= 768;
// logarithmicDepthBuffer (Vince: label + spaken-wireframe blijven flikkeren ondanks
// renderOrder/polygonOffset-fixes): de camera heeft near=0.1/far=2000, een ratio van
// 20.000:1. Standaard (lineaire) depth-buffers verliezen daar het merendeel van hun
// precisie op typische kijkafstanden -> coïncidente/bijna-coïncidente geometrie
// (wireframe-op-spoel, label-op-flens) kan dan niet betrouwbaar uit elkaar gehouden
// worden, ongeacht hoe groot de polygonOffset is. Dit is de standaard, robuuste fix
// voor scenes met zo'n extreme near/far-ratio (herverdeelt de precisie logaritmisch
// i.p.v. lineair), fundamenteler dan nog meer aan de offset-hendel draaien.
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, logarithmicDepthBuffer: true });
// Mobiel-DPR-cap 2->1.5 (Vince: iPhone hapert nog steeds met elke scroll). Eerder gaf
// mobiel juist een HOGERE cap dan desktop (2 vs 1.5) — meer pixels op zwakkere hardware.
// 1.0/1.25 gaven zichtbaar "pixels tellen" en zijn terecht afgekeurd, maar 1.5 is nooit
// apart geprobeerd: het is exact de waarde die desktop (de referentie-ervaring, "Mac")
// al gebruikt en waarvan de scherpte prima is bevonden. Kwadratisch effect (breedte×
// hoogte): 2.0->1.5 = -44% pixels om te shaden, zonder de eerder afgekeurde "blokkerig"-
// drempel te raken.
// Terug naar 2 (Vince: resolutie ging merkbaar achteruit bij 1.5, en de haperingen waren
// er niet minder om — de DPR-verlaging kostte kwaliteit zonder de vloeiendheid te
// verbeteren, dus niet de juiste hendel. Scherpte weegt zwaarder dan deze specifieke
// framerate-tweak.
// Nu naar 3 (Vince: "veel hogere resolutie/kwaliteit"): de échte hapering bleek later een
// forced-layout-bug (journeyMaxScroll() las elke frame .offsetTop) en een --full-h die op
// sommige vensters de verkeerde hoogte kreeg — geen van beide had iets met pixelRatio te
// maken. Met die twee nu gefixt is er vermoedelijk meer ruimte; cap 3 raakt vooral 3x-DPR-
// telefoons (de meeste iPhones), desktop-schermen zitten zelden boven 2x dus die merken dit
// nauwelijks. Kan niet op echte hardware performance-testen vanaf hier — laat weten of dit
// ergens weer gaat haperen.
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3));
renderer.setSize(window.innerWidth, window.innerHeight, false); // false = laat canvas-CSS (100% cover-viewport) de display-grootte bepalen → full-bleed achter iOS-chrome
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();
// ACES-tonemapping (OutputPass) maakt een scene.background donkerder dan de CSS-hex:
// input #1B2A1F kwam er als ~(9,24,14) uit, fors donkerder dan de pure CSS-pine — dáárom
// week de canvas-achtergrond af van loader + body (zichtbaar o.a. achter de iOS-URL-balk).
// Oplossing: scene.background voeden met een vóór-gecompenseerde (lichtere) input die ná
// ACES exact de pure pine/moss oplevert. Empirisch geijkt (zie SCENE_BG_* hieronder):
//   input #2C3A2F → rendert als #1B2A1F (pine) ; input #3C4A42 → rendert als #2F4036 (moss).
// Blueprint-achtergrond: vlakke groene kleur + faint graph-paper grid, gebakken in
// een canvas-texture als scene.background. Zo zit het grid ACHTER de spoel (de opake
// spoel dekt het af) i.p.v. als CSS-laag eroverheen, en het loopt door dezelfde ACES-
// tonemapping als de oude solid-color background. Kleur lerpt mee op scroll (zie
// updateColorTokens), grid-lijnen blijven vast sage.
const bgCanvas = document.createElement('canvas');
const bgCtx = bgCanvas.getContext('2d');
let _bgLastHex = '#2C3A2F';
// Design-switcher (Vince, admin-paneel): meerdere achtergrond-varianten voor de
// hero-canvas, om live te vergelijken wat het blueprint-grid vervangt (baas-
// feedback: een raster leest als 3D-software i.p.v. fysiek filament). Geen
// localStorage-persist (Vince): elke load start op Design 3 (huidige default).
//   1 = blueprint-grid (huidig)   2 = iconmark-patroon (idee baas)
//   3 = layer lines (FDM-printlagen)   4 = wikkel-bogen (spoel-doorsnede)
let _bgDesign = 3;
// Patroon op mobiel volledig UIT (Vince, uit de grid-tijd): na twee rondes clip/fade-
// tuning was de simpelste, juiste oplossing gewoon geen patroon op mobiel — desktop
// houdt 'm (canvas zit daar al in de rechter CSS-kolom, dus tekst zit daar nooit "op"
// het patroon; op mobiel was de canvas nog full-bleed, wat steeds bleef wringen).
// updateColorTokens roept drawBlueprintBg elke scroll-frame aan; her-rasteren is
// alleen nodig als kleur, design of canvas-maat écht wijzigde (de zwaardere
// patronen (2) mogen geen per-frame scroll-kosten toevoegen).
let _bgDrawnKey = '';
// Retourneert true als er écht gerasterd is (false = guard-skip), zodat call-sites
// bgTexture.needsUpdate alleen zetten wanneer er iets te uploaden valt — de
// onvoorwaardelijke needsUpdate uploadde anders elke scroll-frame een ongewijzigde
// multi-megapixel canvas naar de GPU (review 2026-07-17).
function drawBlueprintBg(hex) {
  _bgLastHex = hex;
  const W = bgCanvas.width, H = bgCanvas.height, dpr = bgCanvas._dpr || 1;
  // LIVE METEN EN NIET MOBILE_INIT (2026-08-26). Die constante wordt één keer bij
  // het laden gezet, dus wie het venster van 819 naar 756 sleepte hield de lijnen
  // terwijl de mobiele opmaak allang was ingeschakeld; laadde je dezelfde breedte
  // opnieuw, dan was de achtergrond wél vlak. Precies dat verschil zag Vince.
  // De andere MOBILE_INIT-plekken blijven staan: dat zijn de drumroll-tijden, en
  // die horen bij de choreografie die bij het laden wordt uitgezet.
  const smal = window.innerWidth <= 768;
  // De vlag hoort in de sleutel. Hij loopt in de praktijk mee met W, maar dan
  // hangt het herteken-besluit af van een afgeleide in plaats van de vlag zelf.
  const key = hex + '|' + _bgDesign + '|' + W + 'x' + H + '|' + (smal ? 'm' : 'd');
  if (key === _bgDrawnKey) return false;
  _bgDrawnKey = key;
  bgCtx.fillStyle = hex;
  bgCtx.fillRect(0, 0, W, H);
  if (smal) return true;
  // Crisp lijnen: bij ONEVEN lineWidth hoort het lijncentrum op x.5 (anders smeert
  // 'ie over twee pixelrijen), bij EVEN lineWidth juist op een heel getal — de oude
  // vaste +0.5 maakte 2px-lijnen (dpr 1.5/2) een wazige 3px-band met halve-alpha
  // randen (review 2026-07-17). Coördinaten eerst afronden, dan `off` erbij.
  const lw = Math.max(1, Math.round(dpr));
  const off = (lw % 2) ? 0.5 : 0;
  const line = (alpha, width) => {
    bgCtx.strokeStyle = 'rgba(184, 216, 194, ' + alpha + ')';
    bgCtx.lineWidth = width || lw;
  };
  if (_bgDesign === 2) {
    // Iconmark-patroon: het 3D4M block-icon (vierkant met uitgespaard onderpaneel,
    // zelfde geometrie als #logo-icon in de header: inset 80/1600, paneel vanaf
    // y=800 tot 1520) in een ruim, verspringend raster. Twee passes: eerst alle
    // vierkanten in sage, dan alle uitsparingen in de bg-kleur.
    const S = 28 * dpr, PX = 120 * dpr, PY = 104 * dpr;
    const each = (cb) => {
      for (let row = 0, y = -S; y < H + S; row++, y += PY) {
        const off = (row % 2) ? PX / 2 : 0;
        for (let x = off - PX; x < W + S; x += PX) cb(x, y);
      }
    };
    bgCtx.fillStyle = 'rgba(184, 216, 194, 0.07)';
    each((x, y) => bgCtx.fillRect(x, y, S, S));
    bgCtx.fillStyle = hex;
    each((x, y) => bgCtx.fillRect(x + 0.05 * S, y + 0.5 * S, 0.9 * S, 0.45 * S));
  } else if (_bgDesign === 3) {
    // Layer lines: fijne horizontale lijnen als de printlagen van een FDM-wand;
    // elke 6e lijn iets sterker voor ritme.
    const step = 8 * dpr, accentEvery = 6;
    line(0.045);
    bgCtx.beginPath();
    for (let y = step, i = 1; y <= H; y += step, i++) {
      if (i % accentEvery === 0) continue;
      bgCtx.moveTo(0, Math.round(y) + off); bgCtx.lineTo(W, Math.round(y) + off);
    }
    bgCtx.stroke();
    line(0.10);
    bgCtx.beginPath();
    for (let y = step * accentEvery; y <= H; y += step * accentEvery) {
      bgCtx.moveTo(0, Math.round(y) + off); bgCtx.lineTo(W, Math.round(y) + off);
    }
    bgCtx.stroke();
  } else if (_bgDesign === 4) {
    // Wikkel-bogen: concentrische ringen vanuit de spoel-positie in de hero-stand,
    // als doorsnede van gewikkeld filament. Zelfde ritme als design 1/3: elke 5e
    // ring dik (sterker + dubbele lijndikte), de vier ertussen dun -- geeft
    // diepte i.p.v. een vlak uniform ringenveld (Vince 2026-07-17). Bogen krijgen
    // integer centrum/radius (geen `off`): een curve raakt pixelgrenzen toch
    // overal anders, en de dikke pass heeft een even lijndikte.
    const cx = Math.round(W * 0.5), cy = Math.round(H * 0.55), step = 36 * dpr;
    const maxR = Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy));
    const ringPass = (accent) => {
      bgCtx.beginPath();
      let ring = 0;
      for (let r = 40 * dpr; r <= maxR; r += step, ring++) {
        if ((ring % 5 === 0) !== accent) continue;
        const ri = Math.round(r);
        bgCtx.moveTo(cx + ri, cy);
        bgCtx.arc(cx, cy, ri, 0, Math.PI * 2);
      }
      bgCtx.stroke();
    };
    line(0.055);
    ringPass(false);
    line(0.10, lw * 2);
    ringPass(true);
  } else {
    // Design 1: blueprint-grid (minor + major graph-paper raster).
    const draw = (step, alpha) => {
      line(alpha);
      bgCtx.beginPath();
      for (let x = 0; x <= W; x += step) { bgCtx.moveTo(Math.round(x) + off, 0); bgCtx.lineTo(Math.round(x) + off, H); }
      for (let y = 0; y <= H; y += step) { bgCtx.moveTo(0, Math.round(y) + off); bgCtx.lineTo(W, Math.round(y) + off); }
      bgCtx.stroke();
    };
    draw(32 * dpr, 0.028);  // minor grid
    draw(128 * dpr, 0.056); // major grid
  }
  return true;
}
window.__setBgDesign = function (design) {
  _bgDesign = parseInt(design, 10) || 1;
  drawBlueprintBg(_bgLastHex);
  bgTexture.needsUpdate = true;
  // Forceer meteen één frame: het #scene-canvas is position:fixed en de scroll-
  // render-loop staat STIL zodra je onderaan bent (scrollProgress>=1, zie animate)
  // en kan door de browser gethrottled zijn. Zonder deze render blijft het fixed
  // canvas het oude frame tonen en lijkt de switch niks te doen. (fix 2026-07-07)
  composer.render();
};
function sizeBgCanvas() {
  const dpr = Math.min(window.devicePixelRatio || 1, 3); // zelfde cap als renderer.setPixelRatio
  bgCanvas._dpr = dpr;
  // Math.floor, niet round: three's setSize floort (w * pixelRatio) voor de drawing
  // buffer. Een round-texture is bij fractional dpr (Windows 125/150%, browser-zoom)
  // in de helft van de gevallen 1px groter dan de buffer, en met NearestFilter valt
  // er dan ergens op het scherm exact één texel-rij/kolom weg — op een uniform
  // patroon zichtbaar als een ontbrekende/halve lijn (review 2026-07-17).
  bgCanvas.width = Math.max(2, Math.floor((canvas.clientWidth || window.innerWidth) * dpr));
  bgCanvas.height = Math.max(2, Math.floor((canvas.clientHeight || window.innerHeight) * dpr));
  // Een width/height-toewijzing wist het canvas ALTIJD, ook bij exact dezelfde maat
  // (applyViewportSize op load riep dit nogmaals aan met ongewijzigde maat: canvas
  // leeg, guard-key identiek -> redraw geskipt -> vlakke kolom zonder patroon).
  // Guard dus expliciet invalideren vóór de redraw.
  _bgDrawnKey = '';
  drawBlueprintBg(_bgLastHex);
}
sizeBgCanvas();
const bgTexture = new THREE.CanvasTexture(bgCanvas);
bgTexture.colorSpace = THREE.SRGBColorSpace;
// De echte oorzaak van het "blurry grid" (Vince-feedback): CanvasTexture filtert
// standaard bilineair/trilineair (LinearFilter + mipmaps), wat de scherpe 1-2px
// canvas-lijnen laat vervagen zodra de GPU ze sampelt. De canvas rendert al 1:1 op
// scherm (geen zinvolle min/magnificatie), dus NearestFilter + geen mipmaps geeft
// pixel-scherpe lijnen zonder kwaliteitsverlies elders.
bgTexture.magFilter = THREE.NearestFilter;
bgTexture.minFilter = THREE.NearestFilter;
bgTexture.generateMipmaps = false;
scene.background = bgTexture;

const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

// HDRI environment-map: UIT. Probeersel voor premium material-feel, maar een
// studio-HDRI voedt image-based lighting in álle PBR-oppervlakken en wast de
// spool-kleur uit naar te licht — dat is niet weg te dimmen zonder ook de
// vorm-gevende directional belichting te slopen. Terug naar de bekende-goede
// staat: alleen ambient + key + fill directional lights.
// new EXRLoader().load('./hdri/studio.exr', (tex) => {
//   tex.mapping = THREE.EquirectangularReflectionMapping;
//   const envMap = pmremGenerator.fromEquirectangular(tex).texture;
//   scene.environment = envMap;
//   scene.environmentIntensity = 1.2;
//   tex.dispose();
// });

// Fill-lights — originele waarden (gecalibreerd zonder HDRI). Dit is de look
// die er stond vóór het HDRI-experiment.
const ambient = new THREE.AmbientLight(0xffffff, 0.35);
scene.add(ambient);
const keyLight = new THREE.DirectionalLight(0xffffff, 0.8);
keyLight.position.set(8, 12, 6);
scene.add(keyLight);
const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
fillLight.position.set(-8, 4, 8);
scene.add(fillLight);

const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 2000);

// Postprocessing: bloom-glow op neon emissive + HDR-highlights (RenderPass → Bloom → OutputPass)
// OutputPass doet de tone-mapping + sRGB; renderer.toneMapping blijft ACESFilmic (geen dubbele map)
const composer = new EffectComposer(renderer);
// MSAA: antialias:true op de renderer doet niks zodra we via de composer
// renderen (eindbeeld komt uit een render-target zonder multisampling). 4x
// multisampling geeft de fat-line wireframe-randen edge-AA. (4 i.p.v. 8: de
// polygonOffset killt het z-fighting al, dus 8 was overdaad en woog te zwaar op
// de framerate met het detailrijke model + bloom.)
// Was mobiel 4->2 (Vince: iPhone hapert nog steeds met elke scroll) — de échte hapering
// bleek later een forced-layout-bug (journeyMaxScroll() las elke frame .offsetTop),
// losstaand van MSAA. Nu terug naar 4x overal ("veel hogere resolutie/kwaliteit").
const _msaaSamples = 4;
composer.renderTarget1.samples = _msaaSamples;
composer.renderTarget2.samples = _msaaSamples;
composer.addPass(new RenderPass(scene, camera));
// Bloom-interne resolutie (niet de canvas zelf): was mobiel 0.6x i.p.v. 1x als perf-hendel.
// Nu overal volledige resolutie ("veel hogere resolutie/kwaliteit") — kan hier niet op
// echte hardware performance-testen, laat weten of dit ergens gaat haperen.
const _bloomRes = new THREE.Vector2(window.innerWidth, window.innerHeight);
const bloomPass = new UnrealBloomPass(
  _bloomRes,
  0.18, // strength — glow nog subtieler
  0.35, // radius — iets krapper
  0.9   // threshold — neon emissive + HDR-highlights bloomen; off-white bg net eronder (geen ongewilde glow)
);
// Bloom = de neon-glow, "het speciale" van de animatie. Op álle apparaten aan; mobiel
// uitzetten maakte het beeld vlak en de kleuren dof. Performance halen we niet meer uit
// kwaliteit — voor smooth+mooi op mobiel is een pre-rendered video de juiste route.
composer.addPass(bloomPass);
composer.addPass(new OutputPass());

// Outer-scope refs (zodat animate() ze kan gebruiken)
let model = null;
let spoolMesh = null;
let spoolMatRef = null;
let filamentMatRef = null;
// Wind-on reveal: radiale clip op de filament-fill. De filament-geometrie heeft zijn
// axle langs lokaal-X, dus radius-vanaf-as = length(position.yz). uWindR = de radius
// waaronder filament zichtbaar is, gestuurd per-stop via 'wind' 0..1. R_INNER/R_OUTER
// worden bij load GEMETEN over de echte geometrie (geen gegokte getallen).
const filWindUniforms = { uWindR: { value: 1e9 } }; // start: alles passeert (vol)
let WIND_R_INNER = 25, WIND_R_OUTER = 96;            // fallback; overschreven door meting
// Matte-reliëf op de spoel ingebakken (zie spoolMat.onBeforeCompile + per-stop emissive-lift):
// tilt de bijna-zwarte surface uit de "crush-zone" zodat de welving + korrel óók op true-black
// mini-LED-schermen (MacBook XDR) tonen, niet alleen op matte monitors met grijzige zwarten.
// ── Companion-recede (inc 3): de spoel is geen hoofdrol meer maar een metgezel op de
// achtergrond. Twee tuning-knoppen (0..1, lager = verder naar achteren):
const RECEDE_OPACITY = 1.0; // Vince: model terug naar 100% opacity (was 0.55) — spoel/filament/label niet meer transparant
const RECEDE_GLOW = 0.28;    // schaalt neon-glow + basis-bloom + rand-gloed → minder pop, het groen zakt terug
const spoolGrainUniforms = { uGrainAmp: { value: 0.003 }, uGrainScale: { value: 430.0 }, uRimAmp: { value: 0.45 * RECEDE_GLOW }, uRimPow: { value: 2.6 } }; // korrel + fresnel-rim (premium randgloed)
// Universeel verhoogd (was mobiel-only 0.55/desktop 0.32): Vince ziet zwart i.p.v.
// donkergroen op ZOWEL telefoon als het eigen MacBook-scherm (niet alleen op zijn
// externe matte monitor, die van nature opgetilde zwartwaarden heeft en het altijd
// al goed toonde). Dit is dus geen mobiel-specifiek scherm-verschil maar een te
// subtiele kleur/lift-combinatie voor de meeste "normale" (true-black-capable)
// schermen. spoolColor is daarna nog een stap donkerder gezet ('#26362C', was
// '#2F4036' → '#1E2C23'-origineel): halverwege tussen de te-lichte en te-donkere
// (crushte) poging in, met SPOOL_LIFT als vaste lift-floor eronder.
const SPOOL_LIFT = 0.5; // emissive-intensiteit (× eigen spoel-kleur) als lift-floor
const LABEL_EMISSIVE = 0.55; // label zelf-verlicht (× eigen texture) → crisp + leesbaar in de donkere scène
// Label-tint (Vince: donkergroen i.p.v. zwart-wit, "nog meer een achtergrond ding").
// Genormaliseerde richting van STOPS' spoolColor '#243228' (zelfde donkergroen-token
// als de spoel zelf al gebruikt, geen nieuwe kleur verzonnen) × 1.5 helderheids-schaal
// zodat hooglichten nog leesbaar opladen i.p.v. te snel dicht te slibben.
const LABEL_TINT = new THREE.Vector3(0.735, 1.021, 0.817);
let filRMin = Infinity, filRMax = 0;                 // accumulators tijdens de traverse
let labelMatRef = null; // wit flens-label: eigen materiaal, faadt mee met de spoel
let labelMesh = null;   // het label-mesh zelf — hard verbergen op de achterkant (geen show-through/flikker)
let wrapper = null;
let spinner = null;
let baseCameraZ = 0;
const filamentWireframeOverlays = [];
const spoolWireframeOverlays = [];
const TARGET_OFFSET_X = -70; // shift wereld-origin naar links, spool rechts in beeld
const allLineMaterials = [];

// Bg + tekst lerpen langs scroll (BI v3 named tokens)
// Dark hero-band: bg Pine, ink Paper, ink-mute Sage
// Parking eind:   bg Paper, ink Pine, ink-mute Moss
const BG_DARK = new THREE.Color('#1B2A1F');   // Pine (start) — CSS-token (puur)
const BG_MID = new THREE.Color('#2F4036');    // tussenstop — blijft groen — CSS-token (puur)
const BG_LIGHT = new THREE.Color('#F4F5F0');  // Paper (eind)
// ACES-gecompenseerde tweelingen voor scene.background: deze (lichtere) inputs renderen ná
// OutputPass-tonemapping exact als de pure CSS-tokens hierboven. Empirisch geijkt op pixel-
// niveau (readPixels): #2C3A2F→#1B2A1F, #3C4A42→#2F4036. Houd ze in sync met BG_DARK/BG_MID.
const SCENE_BG_DARK = new THREE.Color('#2C3A2F'); // → rendert als Pine #1B2A1F
const SCENE_BG_MID = new THREE.Color('#3C4A42');  // → rendert als Moss #2F4036
const _sceneBgTmp = new THREE.Color();
const INK_LIGHT = new THREE.Color('#F4F5F0'); // Paper (op dark band)
const INK_DARK = new THREE.Color('#1B2A1F');  // Pine (op paper band)
const MUTE_SAGE = new THREE.Color('#99BEA7'); // ink-mute op dark
const MUTE_FOREST = new THREE.Color('#455E4F'); // ink-mute op paper
const _bgTmp = new THREE.Color();
const _inkTmp = new THREE.Color();
const _muteTmp = new THREE.Color();
let lastBgUpdate = -1;

function textCurve(p) {
  // Tekst switcht pas wanneer de Paper-overlay (p=0.75..1.0) dominant is —
  // ergens rond 65% in de fade-zone — anders zit cardtekst kort op grijs.
  if (p < 0.90) return 0;
  return Math.min(1, (p - 0.90) / 0.10);
}

// Even de hoofdthread vrijgeven, zodat een tik tussendoor meteen aan de beurt komt.
const yieldToMain = () => (window.scheduler && typeof window.scheduler.yield === 'function')
  ? window.scheduler.yield()
  : new Promise((r) => setTimeout(r, 0));

// Randen van het model, in stukjes (INP, 2026-11-03). Hetzelfde algoritme als THREE.EdgesGeometry
// (r160, regel voor regel, dus dezelfde lijnen in dezelfde volgorde), maar na elke ~30 ms een
// pauze. In één keer kostte dit bij 10x vertraagde CPU ~770 ms aan één stuk, vlak voordat de
// spoel verschijnt; wie dan de cookiemelding of het menu aantikte, wachtte tot bijna een seconde.
const _edgeTri = new THREE.Triangle();
const _edgeNormal = new THREE.Vector3();
const _edgeV0 = new THREE.Vector3();
const _edgeV1 = new THREE.Vector3();
async function edgePositions(geometry, thresholdAngle) {
  const precision = Math.pow(10, 4);
  const thresholdDot = Math.cos(THREE.MathUtils.DEG2RAD * thresholdAngle);
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  const indexCount = indexAttr ? indexAttr.count : positionAttr.count;
  const indexArr = [0, 0, 0];
  const vertKeys = ['a', 'b', 'c'];
  const hashes = new Array(3);
  const edgeData = {};
  const vertices = [];
  let slice = performance.now();
  for (let i = 0; i < indexCount; i += 3) {
    if ((i % 3000) === 0 && performance.now() - slice > 30) { await yieldToMain(); slice = performance.now(); }
    if (indexAttr) {
      indexArr[0] = indexAttr.getX(i);
      indexArr[1] = indexAttr.getX(i + 1);
      indexArr[2] = indexAttr.getX(i + 2);
    } else {
      indexArr[0] = i;
      indexArr[1] = i + 1;
      indexArr[2] = i + 2;
    }
    const { a, b, c } = _edgeTri;
    a.fromBufferAttribute(positionAttr, indexArr[0]);
    b.fromBufferAttribute(positionAttr, indexArr[1]);
    c.fromBufferAttribute(positionAttr, indexArr[2]);
    _edgeTri.getNormal(_edgeNormal);
    hashes[0] = `${Math.round(a.x * precision)},${Math.round(a.y * precision)},${Math.round(a.z * precision)}`;
    hashes[1] = `${Math.round(b.x * precision)},${Math.round(b.y * precision)},${Math.round(b.z * precision)}`;
    hashes[2] = `${Math.round(c.x * precision)},${Math.round(c.y * precision)},${Math.round(c.z * precision)}`;
    if (hashes[0] === hashes[1] || hashes[1] === hashes[2] || hashes[2] === hashes[0]) continue;
    for (let j = 0; j < 3; j++) {
      const jNext = (j + 1) % 3;
      const vecHash0 = hashes[j];
      const vecHash1 = hashes[jNext];
      const v0 = _edgeTri[vertKeys[j]];
      const v1 = _edgeTri[vertKeys[jNext]];
      const hash = `${vecHash0}_${vecHash1}`;
      const reverseHash = `${vecHash1}_${vecHash0}`;
      if (reverseHash in edgeData && edgeData[reverseHash]) {
        if (_edgeNormal.dot(edgeData[reverseHash].normal) <= thresholdDot) {
          vertices.push(v0.x, v0.y, v0.z);
          vertices.push(v1.x, v1.y, v1.z);
        }
        edgeData[reverseHash] = null;
      } else if (!(hash in edgeData)) {
        edgeData[hash] = { index0: indexArr[j], index1: indexArr[jNext], normal: _edgeNormal.clone() };
      }
    }
  }
  await yieldToMain();
  slice = performance.now();
  let keyCount = 0;
  for (const key in edgeData) {
    if ((++keyCount % 2000) === 0 && performance.now() - slice > 30) { await yieldToMain(); slice = performance.now(); }
    if (edgeData[key]) {
      const { index0, index1 } = edgeData[key];
      _edgeV0.fromBufferAttribute(positionAttr, index0);
      _edgeV1.fromBufferAttribute(positionAttr, index1);
      vertices.push(_edgeV0.x, _edgeV0.y, _edgeV0.z);
      vertices.push(_edgeV1.x, _edgeV1.y, _edgeV1.z);
    }
  }
  await yieldToMain();
  return new Float32Array(vertices);
}

async function makeFatLine(geometry, color, linewidth, dense, maxOpacity = 1.0, threshold = 40) {
  const positions = dense
    ? new THREE.WireframeGeometry(geometry).attributes.position.array
    // Threshold (graden): alleen de echte feature-randen, niet elke micro-facet van
    // het detailrijke model. Hoger = schoner/sparser, meer technische-tekening-look.
    : await edgePositions(geometry, threshold);
  const lineGeom = new LineSegmentsGeometry();
  lineGeom.setPositions(positions);
  await yieldToMain();
  const mat = new LineMaterial({
    color,
    linewidth,
    transparent: true,
    opacity: 0,
    depthTest: true,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
  });
  mat.userData.maxOpacity = maxOpacity;
  const line = new LineSegments2(lineGeom, mat);
  line.computeLineDistances();
  // Flicker-fix (Vince-feedback: "witte lijnen flikkeren heel erg"): de lijn EN de
  // mesh waarop 'ie ligt zijn allebei transparent:true, dus Three.js sorteert ze in
  // dezelfde transparante queue op afstand-tot-camera. Bij coïncidente geometrie is
  // die afstand vrijwel identiek -> instabiele sort-volgorde -> de winnaar wisselt
  // elk frame (het echte "flikkeren", eerder dan alleen z-fighting). renderOrder is
  // de PRIMAIRE sorteersleutel in Three.js (vóór afstand), dus dit forceert de lijn
  // altijd na de mesh te tekenen — deterministisch, geen wisselende volgorde meer.
  line.renderOrder = 1;
  mat.userData.line = line; // voor de visibility-cull in animate (skip draw bij opacity 0)
  allLineMaterials.push(mat);
  return { line, mat };
}

// ── Voorraad-aankomst (enige toevoeging t.o.v. het origineel): aan het laatste
// station kijk je ingezoomd tegen de kopse kant (flens) van de spoel aan, en
// blijkt 'ie één-van-velen in een gepakte rij identieke rollen — de voorraad,
// klaar voor verkoop. De buren zijn instanced kopieën van het ECHTE model
// (samengevoegde geometrie → 2 draw-calls). De camera wordt langs de flens-as
// gericht (programmatisch face-on). Eenzijdig grid (rechts) zodat de tekst
// links leesbaar blijft.
const STOCK = { cols: 5, rows: 3, packK: 1.03, shown: false }; // shown: zie buildStock()
let stockSpoolParts = [], stockFilParts = [];     // bron-meshes uit het model
let stockSpoolInst = null, stockFilInst = null;
let stockSpoolMat = null, stockFilMat = null;
let stockCount = 0;
const STOCK_OFF = [];                              // {ox, oy} per instance (hero-cel uitgesloten)
const STOCK_AXIS_LOCAL = new THREE.Vector3(1, 0, 0); // flens-as in spoel-geometry-lokaal
let STOCK_FLANGE_DIAM = 1;
const STOCK_UP = new THREE.Vector3(0, 1, 0);
const _stockAxis = new THREE.Vector3(), _stockCenter = new THREE.Vector3();
const _stockRight = new THREE.Vector3(), _stockUp = new THREE.Vector3();
const _faceCamPos = new THREE.Vector3(), _lookCur = new THREE.Vector3();
const _journeyCamPos = new THREE.Vector3(), _faceLook = new THREE.Vector3();
const _camDir = new THREE.Vector3();  // camera-richting: tussen flens-as (kopse) en _stockRight (zij)
const _camRight = new THREE.Vector3(), _camUp = new THREE.Vector3(); // camera-schermassen voor Hero-offsets
// Geometrisch herberekend (niet nog een procent-tweak): de hoekgrootte-formule
// atan(1/(2*faceK)) is onafhankelijk van de absolute flens-diameter (die valt weg
// tegen zichzelf), dus dit is een harde berekening, geen schatting. Alle vier de
// STOPS-stations (Hero, Engineering, Research, Parking) delen nu dezelfde 90%-
// vulling van de 35°-FOV (faceK ≈ 1.773) — was voorheen alleen zo voor Hero/Parking;
// Engineering/Research (dit fallback-getal) stond nog op ~36% vulling (faceK 4.555),
// vandaar dat die twee stations ineens "eeniemienie" oogden zodra Hero/Parking
// groter werden. Zelfde getal overal = geen springerige maat meer tijdens de scroll.
const SINGLE_FACE_K = 1.773;         // face-on afstand voor één rol tijdens het vullen (× flens-diameter) — 90% vulling van de 35°-FOV, gelijk aan Hero/Parking
let _faceOnDbg = 0, _faceDotDbg = 0; // debug: face-on factor + view-dir·flens-as
const _tmpV = new THREE.Vector3(), _tmpOff = new THREE.Vector3();
const _stockM = new THREE.Matrix4();
const _rackProj = new THREE.Vector3();
// Per-rol as-spin in de kast: elke filamentrol draait individueel om zijn eigen
// flens-as (in-place), scroll-gekoppeld (niets vanzelf). Eigen vaste fase +
// lichte scroll-gekoppelde snelheidsvariatie zodat ze niet als één blok mee-
// tuimelen. Wereld-space rotatie → onafhankelijk van hierarchie.
const GOLDEN = 2.399963229728653;                    // gulden hoek → fase per rol
const _spinRot = new THREE.Matrix4(), _spinPivot = new THREE.Matrix4();
const _rollC = new THREE.Vector3();
let _stockSpin0 = 0;                                  // debug: as-hoek van buur 0

// Merge een lijst meshes tot één geometry in MODEL-lokale space (position+normal,
// non-indexed → gegarandeerd mergebaar). Toepassen van model.matrixWorld plaatst
// 'm later exact als de hero.
function mergeParts(parts) {
  if (!parts.length) return null;
  const inv = new THREE.Matrix4().copy(model.matrixWorld).invert();
  const geos = parts.map(mz => {
    const g = mz.geometry.clone().toNonIndexed();
    g.applyMatrix4(new THREE.Matrix4().copy(inv).multiply(mz.matrixWorld));
    const k = new THREE.BufferGeometry();
    k.setAttribute('position', g.getAttribute('position'));
    if (g.getAttribute('normal')) k.setAttribute('normal', g.getAttribute('normal'));
    else { k.computeVertexNormals(); }
    return k;
  });
  return mergeGeometries(geos, false);
}

function buildStock() {
  model.updateMatrixWorld(true);
  // Flens-as (kleinste bbox-dim) + flens-diameter (grootste twee dims)
  spoolMesh.geometry.computeBoundingBox();
  const d = new THREE.Vector3(); spoolMesh.geometry.boundingBox.getSize(d);
  const dims = [['x', d.x], ['y', d.y], ['z', d.z]].sort((a, b) => a[1] - b[1]);
  STOCK_AXIS_LOCAL.set(dims[0][0] === 'x' ? 1 : 0, dims[0][0] === 'y' ? 1 : 0, dims[0][0] === 'z' ? 1 : 0);
  STOCK_FLANGE_DIAM = Math.max(dims[1][1], dims[2][1]);
  // Grid-offsets: hero op (ox 0, oy 0), alle buren naar screen-rechts (ox<0)
  const rMid = Math.round((STOCK.rows - 1) / 2);
  for (let r = 0; r < STOCK.rows; r++) for (let c = 0; c < STOCK.cols; c++) {
    if (c === 0 && r === rMid) continue; // hero-cel
    STOCK_OFF.push({ ox: -c, oy: rMid - r });
  }
  stockCount = STOCK_OFF.length;
  // De voorraadkast is geskipt (rackIn = 0 in animate, "alt3"), dus de kopieën komen nooit in
  // beeld. Ze toch bouwen kostte bij 10x vertraagde CPU ~430 ms aan één stuk vlak voor de spoel
  // verschijnt (INP, 2026-11-03). De maten hierboven blijven: die gebruikt de camera. Komt de
  // kast terug, zet STOCK.shown dan op true.
  if (!STOCK.shown) return;
  const spoolGeom = mergeParts(stockSpoolParts);
  const filGeom = mergeParts(stockFilParts);
  // Materialen = het originele product-uiterlijk (sage spoel / cream filament)
  stockSpoolMat = new THREE.MeshStandardMaterial({ color: new THREE.Color('#6F7C75'), roughness: 0.6, metalness: 0.1, transparent: true, opacity: 0 });
  stockFilMat = new THREE.MeshStandardMaterial({ color: new THREE.Color('#A89880'), roughness: 0.75, metalness: 0.0, transparent: true, opacity: 0 });
  if (spoolGeom) { stockSpoolInst = new THREE.InstancedMesh(spoolGeom, stockSpoolMat, stockCount); stockSpoolInst.frustumCulled = false; stockSpoolInst.visible = false; scene.add(stockSpoolInst); }
  if (filGeom) { stockFilInst = new THREE.InstancedMesh(filGeom, stockFilMat, stockCount); stockFilInst.frustumCulled = false; stockFilInst.visible = false; scene.add(stockFilInst); }
}

function makeSpoolNormalMap() {
  const size = 512;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      // Plastic-korrel + brede zachte golving (zichtbaar reliëf)
      const grainX = (Math.random() - 0.5) * 0.5;
      const grainY = (Math.random() - 0.5) * 0.5;
      const waveX = Math.sin(x * 0.04) * 0.07;
      const waveY = Math.cos(y * 0.04) * 0.07;
      data[i + 0] = Math.floor(128 + (grainX + waveX) * 110);
      data[i + 1] = Math.floor(128 + (grainY + waveY) * 110);
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.needsUpdate = true;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 3);
  return tex;
}

// Genereer planar UV's op de twee grootste assen (= het flensvlak), zodat de normal map zichtbaar wordt
function ensurePlanarUV(geo) {
  geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const d = { x: bb.max.x - bb.min.x, y: bb.max.y - bb.min.y, z: bb.max.z - bb.min.z };
  const [u, v] = ['x', 'y', 'z'].sort((a, b) => d[b] - d[a]); // twee grootste assen
  const U = u.toUpperCase(), V = v.toUpperCase();
  const pos = geo.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2]     = (pos['get' + U](i) - bb.min[u]) / (d[u] || 1);
    uv[i * 2 + 1] = (pos['get' + V](i) - bb.min[v]) / (d[v] || 1);
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

// Scroll progress (met URL-debug override: ?scroll=0.5 voor preview)
let scrollProgress = 0;
let _capProgress = null; // opname-modus (?capture): forceert scrollProgress als ramp 0→1
// Eenmalig: zodra je de hele animatie hebt doorlopen (catalogus bereikt) gaat de
// snap eraf en wordt de scroll overal native — ook als je daarna weer omhoog door
// de animatie scrollt (die speelt dan gewoon scroll-gekoppeld mee, zonder snap).
let journeyCompleted = false;
const urlScrollOverride = new URLSearchParams(window.location.search).get('scroll');
const SCROLL_DEBUG = urlScrollOverride !== null ? parseFloat(urlScrollOverride) : null;
// ?debug=1: voorraad-state (face-on-check + scherm-%) naar #stats voor headless
// verificatie zonder WebGL-raster.
const STORY_DEBUG = new URLSearchParams(window.location.search).has('debug');

// Forceer scroll naar bovenaan bij reload (geen browser scroll-restoration)
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}
// Bij het load-event nog een keer naar boven, maar niet als de bezoeker dan al scrolt: sinds
// de inhoud meteen verschijnt kan dat ruim vóór het load-event, en dan sprong de pagina terug.
let _userScrolled = false;
['wheel', 'touchmove', 'keydown'].forEach(ev => window.addEventListener(ev, () => { _userScrolled = true; }, { passive: true, once: true }));
// Een anker in het adres gaat voor. Na de nieuwsbrief-aanmelding in de footer brengt Shopify je
// terug op /?customer_posted=true#footer-signup, en dan zette dit je bovenaan en zag je de
// bevestiging niet (Vince, 2026-10-27). Offset 80 = scroll-margin-top van .section.
const _hashTarget = (() => {
  try { return window.location.hash ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null; }
  catch (_) { return null; }
})();
const _startScroll = () => lenis.scrollTo(_hashTarget || 0, { immediate: true, ...(_hashTarget ? { offset: -80 } : {}) });
_startScroll();
window.addEventListener('load', () => { if (!_userScrolled) _startScroll(); });

// 3D-journey loopt van scrollY=0 (Hero) tot parking-band.offsetTop + 50vh (eind
// van de fade-zone, waar de cards beginnen). Daarboven blijft de animation in
// haar eindstaat — parking-cards zijn free-scroll.
const JOURNEY_SECTIONS = 3; // Hero, Engineering(draaien+vullen), Research(kast) — Products verwijderd (alt3)
// Page-Y (absolute) van een element. .step elementen zitten binnen
// .scroll-container die position:relative is, dus offsetTop is daar lokaal —
// we moeten via offsetParent-chain klimmen voor de echte page-Y.
function pageOffsetTop(el) {
  let y = 0;
  while (el) {
    y += el.offsetTop;
    el = el.offsetParent;
  }
  return y;
}
// Journey loopt van scrollY=0 (echte page-top) tot parking-top — NIET vanaf
// hero-top. Masthead+announcement-bar staan met opzet in de normale flow boven
// de hero (chrome scrollt weg i.p.v. over de animatie te glijden), maar een
// vroegere hero-top-offset maakte die hele chrome-hoogte een dode zone: pas ná
// het wegscrollen van de header begon de spoel te reageren (Vince: "hij beweegt
// echt niks aan het begin"). Nu telt elke scroll-pixel vanaf de allereerste mee.
// Gecached (i.p.v. elke rAF-frame een .offsetTop-read, wat een forced layout
// triggert): op mobiel bijgedragen aan het "haperen" dat Vince meldde — 60x/sec
// een synchrone reflow forceren is precies het soort werk dat framedrops geeft.
// Ververst op load/resize + elke 500ms (vangt late layout-shifts, bv. logo's/
// fonts die laat laden), net als _heroTxtBotFrac hieronder.
let _journeyMaxCache = null;
function computeJourneyMax() {
  // De Filaments-sectie (#pg2) is de structurele grens waar de journey eindigt --
  // daar begint de free-scroll catalogus. (De oude .parking-band-variant + de
  // admin-toggle die ertussen wisselde zijn verwijderd; #pg2 is nu de enige.)
  const pb = document.getElementById('pg2');
  _journeyMaxCache = pb ? Math.max(1, pb.offsetTop) : JOURNEY_SECTIONS * window.innerHeight;
}
function journeyMaxScroll() {
  if (_journeyMaxCache === null) computeJourneyMax();
  return _journeyMaxCache;
}
computeJourneyMax();
setInterval(computeJourneyMax, 500);
window.addEventListener('resize', computeJourneyMax);

function updateScroll() {
  if (_capProgress !== null) { scrollProgress = _capProgress; return; } // opname-ramp wint
  if (SCROLL_DEBUG !== null) {
    scrollProgress = SCROLL_DEBUG;
    return;
  }
  const max = journeyMaxScroll();
  scrollProgress = max > 0 ? Math.min(1, Math.max(0, getScrollY() / max)) : 0;

  // Catalogus bereikt = animatie doorlopen.
  if (scrollProgress >= 1) journeyCompleted = true;
  // Vanaf sectie 4 (catalogus, scrollProgress >= 1) volledig native: daar draait
  // geen scroll-gekoppelde 3D (render gepauzeerd), dus lerp 1.0 = gewone pagina-
  // scroll zonder dat iets schokt. In de animatie een lichte snappy smoothing
  // (0.18): net genoeg om de 3D vloeiend te interpoleren, dichter bij native dan
  // de oude 0.1.
  lenis.options.lerp = scrollProgress >= 1 ? 1.0 : 0.18;
}
updateScroll();
// rAF-throttle: scroll-events kunnen 100+x/sec triggeren op smooth trackpads;
// we hoeven scrollProgress max één keer per paint-frame te updaten.
function rafThrottle(fn) {
  let scheduled = false;
  return function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      fn();
    });
  };
}
scrollTarget.addEventListener('scroll', rafThrottle(updateScroll), { passive: true });

// Scroll is volledig vrij (Lenis smooth-scroll, geen snap). De 3D-animatie
// is continu gekoppeld aan scrollProgress, dus de verhaallijn loopt mee met
// elke scroll-positie zonder dat de gebruiker ergens vastzit.
//
// parkingTopY blijft als helper voor scroll-rail / journey-berekening.
function parkingTopY() {
  const pb = document.getElementById('pg2');
  if (pb) return pageOffsetTop(pb);
  return JOURNEY_SECTIONS * window.innerHeight;
}

// === Scroll keyframes (de journey) ===
// Per stop: scale, modelPosX (links/rechts shift), targetY (camera kijkhoogte),
// camDist (zoom in/uit), tiltX (kantelhoek), spoolOpacity (fade)
// Keyframes uitgelijnd op snap-scroll posities (1/3 stappen voor 4 sections)
// camDir is een unit-vector voor camera-positie relatief tot target (x, y, z)
// Per-stop material states:
// spoolOpacity (solid fill), spoolWire (wireframe), spoolColor (hex), filColor (hex), filGlow (neon emissive), filWire (wireframe), filOpacity
// 5 secties: Hero, Engineering, Research, Products, Parking — Parking eindigt op wit + model onzichtbaar
// Per-stop kleuren: spoolColor + filColor (hex), filGlow = neon emissive 0-1
const STOPS = [
  // Hero (sect 0, p=0): spool naar rechts + omlaag zodat tekst-zone links vrij donker is, lichte 3/4-hoek voor diepte.
  // faceK 1.773->1.478 (Vince: 20% groter bij de start) — alleen Hero, niet de andere
  // stations (die delen nog steeds 1.773 via SINGLE_FACE_K/Parking).
  { p: 0.000, scale: 1.2, posX: 85,   targetY: 120, camDist: 0.58, camDirX: 0.2,  camDirY: 0.08, camDirZ: 0.97, tiltX: 0, tiltZ: 0,    spoolOpacity: 0.85, spoolWire: 1.0, spoolColor: '#26362C', filColor: '#1A271F', filGlow: 0.25, filWire: 0.0, filOpacity: 0.78, wind: 0.15, view: 0, faceK: 1.478, lowY: 0.5, rightX: 0.3 },
  // Engineering (sect 1, p=0.333): DRAAIEN + VULLEN tegelijk — van kopse/leeg naar zij/vol (merge oude st2+st3, alt3)
  { p: 0.333, scale: 0.95, posX: 110, targetY: 0,  camDist: 0.85, camDirX: 0.9, camDirY: 0.2, camDirZ: 0.4, tiltX: 0, tiltZ: 0, spoolOpacity: 0.85, spoolWire: 0.7, spoolColor: '#26362C', filColor: '#3CB96E', filGlow: 1.1, filWire: 0.0, filOpacity: 0.85, wind: 1.0, view: 1.2, rightX: 0.6 },
  // Research (sect 2, p=0.667): GEEN kast — nog een beetje rustig+smooth doordraaien (view 1->1.15), vol + neon-gloed blijft (alt3)
  // Bewust GEEN lowY hier (blijft 0/default): Engineering->Research draait de camera nog
  // fors (view 1.2->2.0, ang 108°->180°) — een lowY-ramp gelijktijdig met die draai bleek
  // het model tijdens de scroll steeds hoger te duwen tot-ie er al vóór het einde uit
  // liep (Vince: "steeds hoger... uit beeld voor de animatie klaar is"). Research->Parking
  // heeft een CONSTANTE view (2.0->2.0, geen rotatie), dus de lowY-ramp staat pas ná dit
  // punt (zie Parking) — daar is het effect wél voorspelbaar/lineair.
  { p: 0.667, scale: 1.0, posX: 110, targetY: 0,  camDist: 0.85, camDirX: 0.85, camDirY: 0.2, camDirZ: 0.4, tiltX: 0, tiltZ: 0, spoolOpacity: 1.0, spoolWire: 0.15, spoolColor: '#26362C', filColor: '#3CB96E', filGlow: 0.85, filWire: 0.0, filOpacity: 0.95, wind: 1.0, view: 2.0, rightX: 0.6 },
  // Parking-final (p=1.0): bg lerpt naar Paper, model fade naar onzichtbaar terwijl de fade-zone passeert.
  // lowY: model schuift omhoog in beeld tijdens Research->Parking (view is hier constant,
  // dus geen rotatie-interactie) zodat de drumroll-glow (p 0.63->1.0) boven de onderrand
  // blijft waar .parking-band straks gaat dekken — Vince wil de glow-timing zelf niet
  // aangepast zien, dus de fix zit in de framing, niet in DR_START/DR_END.
  // faceK 1.773->1.2 (was gelijk aan Research): Vince wil 'm na het laatste station
  // (Research) nog een stuk groter zien worden tijdens het scrollen — ~90% -> ~130%
  // vulling van de FOV, lerpt geleidelijk mee met dezelfde Research->Parking-segment.
  { p: 1.000, scale: 1.2, posX: 0,    targetY: 0,  camDist: 0.65, camDirX: 0.4,  camDirY: 0.25,camDirZ: 0.85, tiltX: 0, tiltZ: 0,    spoolOpacity: 0.0, spoolWire: 0.0, spoolColor: '#26362C', filColor: '#A89880', filGlow: 0.0, filWire: 0.0, filOpacity: 0.0, wind: 1.0, view: 2.0, faceK: 1.2, lowY: -0.6 },
];

// Pre-parse kleuren naar THREE.Color (één keer); _out temps voor de per-frame lerp
STOPS.forEach(s => {
  s._spoolCol = new THREE.Color(s.spoolColor);
  s._filCol = new THREE.Color(s.filColor);
});
const _spoolColOut = new THREE.Color();
const _filColOut = new THREE.Color();

function lerp(a, b, t) { return a + (b - a) * t; }

function getStopState(progress) {
  let from = STOPS[0];
  let to = STOPS[STOPS.length - 1];
  for (let i = 0; i < STOPS.length - 1; i++) {
    if (progress >= STOPS[i].p && progress <= STOPS[i + 1].p) {
      from = STOPS[i];
      to = STOPS[i + 1];
      break;
    }
  }
  // Lineair (geen smoothstep): de easing hoort bij de scroll-glide (Lenis bij snap,
  // smoothed input bij vrij scrollen), niet nóg een keer bij de pose. De dubbele
  // demping — smoothstep én easeOut gaan béide naar nul-snelheid precies óp de stop
  // — gaf het extreem trage laatste stukje + de "doem doem"-jank net voor elke stap.
  const t = Math.min(1, Math.max(0, (progress - from.p) / Math.max(0.0001, to.p - from.p)));
  return {
    scale: lerp(from.scale, to.scale, t),
    posX: lerp(from.posX, to.posX, t),
    targetY: lerp(from.targetY, to.targetY, t),
    camDist: lerp(from.camDist, to.camDist, t),
    camDirX: lerp(from.camDirX, to.camDirX, t),
    camDirY: lerp(from.camDirY, to.camDirY, t),
    camDirZ: lerp(from.camDirZ, to.camDirZ, t),
    tiltX: lerp(from.tiltX, to.tiltX, t),
    tiltZ: lerp(from.tiltZ || 0, to.tiltZ || 0, t),
    spoolOpacity: lerp(from.spoolOpacity, to.spoolOpacity, t),
    spoolWire: lerp(from.spoolWire || 0, to.spoolWire || 0, t),
    spoolCol: _spoolColOut.copy(from._spoolCol).lerp(to._spoolCol, t),
    filGlow: lerp(from.filGlow || 0, to.filGlow || 0, t),
    filWire: lerp(from.filWire || 0, to.filWire || 0, t),
    filCol: _filColOut.copy(from._filCol).lerp(to._filCol, t),
    filOpacity: lerp(from.filOpacity ?? 1.0, to.filOpacity ?? 1.0, t),
    wind: lerp(from.wind ?? 1, to.wind ?? 1, t),
    view: lerp(from.view ?? 0, to.view ?? 0, t),
    faceK: lerp(from.faceK ?? SINGLE_FACE_K, to.faceK ?? SINGLE_FACE_K, t),
    lowY: lerp(from.lowY ?? 0, to.lowY ?? 0, t),
    rightX: lerp(from.rightX ?? 0, to.rightX ?? 0, t),
  };
}

// Load model. Spool-detailed.glb = de detailrijke Blender-spoel (Draco-
// gecomprimeerd, ~5.5MB, gedecimeerd van 4M naar ~167K verts). Oude flat-black
// model staat nog als Product-merged-opt.glb + Product-merged-opt.BACKUP.glb in
// de repo (+ git-tag homepage-animation-stable-2026-06-06) voor revert.
const loader = new GLTFLoader();
loader.setMeshoptDecoder(MeshoptDecoder); // (legacy, oude GLB) — harmless
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://unpkg.com/three@0.160.0/examples/jsm/libs/draco/');
loader.setDRACOLoader(dracoLoader);
const nextFrame = () => new Promise(r => requestAnimationFrame(r));

tmark('glbStart');
loader.load(
  (typeof window !== 'undefined' && window.HP_GLB_URL) || './Spool-detailed.glb',
  async (gltf) => {
    tmark('glbLoaded');
    let step = 'start';
    try {
      step = 'find-node';
      await nextFrame();

      let mergedNode = null;
      gltf.scenes.forEach(scene => {
        scene.traverse(child => {
          if (child.name && child.name.toLowerCase().includes('merged')) {
            mergedNode = child;
          }
        });
      });

      if (!mergedNode) {
        setLoaderIndeterminate('No merged node');
        return;
      }

      step = 'transform';
      await nextFrame();
      mergedNode.position.set(0, 0, 0);
      mergedNode.quaternion.identity();
      model = new THREE.Group();
      model.name = 'spool-root';
      model.add(mergedNode);

      // Basis-rotatie voor het Spool-detailed model: 90° om X + 90° om Y zet de
      // Blender-spoel in dezelfde oriëntatie als het oude model waar de camera-
      // keyframes op getuned zijn (3/4-zicht op de flens-kant met spokes).
      mergedNode.rotation.set(Math.PI / 2, Math.PI / 2, 0);

      step = 'normalmap';
      await nextFrame();
      const normalMap = makeSpoolNormalMap();

      step = 'materials';
      await nextFrame();
      const spoolMat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color('#383838'),
        roughness: 0.55,        // iets gladder voor een verfijnde, duurdere sheen (was 0.62)
        metalness: 0.1,         // plastic is geen metaal → welving toont diffuse gradient
        clearcoat: 0.45,        // wat meer premium coat-sheen (was 0.35)
        clearcoatRoughness: 0.35,
        envMapIntensity: 1.0,
        normalMap,
        normalScale: new THREE.Vector2(1.0, 1.0), // sterker reliëf
        transparent: true,
        opacity: 1,
        // Duw de solide surface naar achteren in z, zodat de coïncidente wireframe-
        // overlay altijd de diepte-test wint. Factor/units iets ruimer (1->3, samen met
        // de renderOrder-fix in makeFatLine) tegen het flikkeren op de bolle, hoog-
        // getesselleerde spoel-surface — 1 was op de rand van te subtiel.
        polygonOffset: true,
        polygonOffsetFactor: 3,
        polygonOffsetUnits: 3,
      });
      spoolMatRef = spoolMat;
      // Fijne procedurele korrel op de emissive (zelf-verlicht → altijd zichtbaar, ook in
      // donkere delen). Object-space (vGrainP=position) dus de spikkel "plakt" op de surface
      // en draait mee = matte reliëf-textuur i.p.v. schermkorrel.
      spoolMat.onBeforeCompile = (shader) => {
        shader.uniforms.uGrainAmp = spoolGrainUniforms.uGrainAmp;
        shader.uniforms.uGrainScale = spoolGrainUniforms.uGrainScale;
        shader.uniforms.uRimAmp = spoolGrainUniforms.uRimAmp;
        shader.uniforms.uRimPow = spoolGrainUniforms.uRimPow;
        const vB = shader.vertexShader, fB = shader.fragmentShader;
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vGrainP;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvGrainP = position;');
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform float uGrainAmp;\nuniform float uGrainScale;\nuniform float uRimAmp;\nuniform float uRimPow;\nvarying vec3 vGrainP;\nfloat flHash(vec3 p){ p=fract(p*0.3183099+0.1); p*=17.0; return fract(p.x*p.y*p.z*(p.x+p.y+p.z)); }')
          .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\nfloat _gn = flHash(floor(vGrainP * uGrainScale));\ntotalEmissiveRadiance += (_gn - 0.42) * uGrainAmp;\nfloat _fr = pow(clamp(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 0.0, 1.0), uRimPow);\ntotalEmissiveRadiance += _fr * vec3(0.45, 0.6, 0.5) * uRimAmp;');
        if (shader.vertexShader === vB || shader.fragmentShader === fB) console.error('SPOOLGRAIN: shader-anchor mismatch — korrel NIET geinjecteerd');
      };
      spoolMat.customProgramCacheKey = () => 'spool-grain';
      const filamentMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color('#A89880'),
        roughness: 0.75,
        metalness: 0.0,
        emissive: new THREE.Color('#3CB96E'),
        emissiveIntensity: 0,
        transparent: false,
        opacity: 1,
        // Zelfde z-offset als de spoel (zie spoolMat): surface naar achteren, wireframe wint.
        polygonOffset: true,
        polygonOffsetFactor: 3,
        polygonOffsetUnits: 3,
      });
      filamentMatRef = filamentMat;
      // Radiale wind-reveal: discard filament-fragmenten buiten uWindR (geometry-lokale
      // axle-radius = length(position.yz), axle = X). Gezet vóór de prewarm-compile zodat
      // de variant gewarmd wordt en er geen recompile-hitch is mid-scroll.
      filamentMat.onBeforeCompile = (shader) => {
        shader.uniforms.uWindR = filWindUniforms.uWindR;
        const vBefore = shader.vertexShader, fBefore = shader.fragmentShader;
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying float vWindRadius;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWindRadius = length(position.yz);');
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform float uWindR;\nvarying float vWindRadius;')
          .replace('#include <clipping_planes_fragment>', 'if (vWindRadius > uWindR) discard;\n#include <clipping_planes_fragment>');
        if (shader.vertexShader === vBefore || shader.fragmentShader === fBefore)
          console.error('WINDREVEAL: shader-anchor mismatch — radiale reveal NIET geinjecteerd');
      };
      filamentMat.customProgramCacheKey = () => 'fil-windreveal'; // één vaste variant

      step = 'traverse';
      await nextFrame();
      let toriCount = 0;
      let totalMeshes = 0;
      const meshList = [];
      model.traverse((child) => {
        if (child.isMesh) {
          totalMeshes++;
          meshList.push(child);
        }
      });

      step = 'wireframes';
      await nextFrame();
      for (let i = 0; i < meshList.length; i++) {
        const child = meshList[i];
        const matName = child.material?.name || '<none>';
        if (matName.toLowerCase().includes('label')) {
          // Wit sticker-label op de flens (SpoolMesh-primitive #2, LabelWhite).
          // Hoort niet bij het filament: het moet meefaden met de SPOEL, dus weg
          // bij Engineering/Research en terug bij de Products-reveal. Eigen
          // materiaal behouden (blijft wit), transparant maken voor de fade,
          // géén wireframe — moet daar gewoon verdwijnen, niet als skelet tonen.
          child.material.transparent = true;
          child.material.opacity = 1;
          labelMatRef = child.material;
          labelMesh = child;
          // Flicker-fix (Vince: "nu flikkert vooral het label" — na de renderOrder-fix op de
          // spaken-wireframe verschoof het naar het label): zelfde oorzaak, andere plek. Het
          // label ligt vrijwel coïncident op het spoel-flens-oppervlak; beide zijn
          // transparent:true, dus Three.js sorteert ze in dezelfde queue op afstand-tot-
          // camera — bij bijna-gelijke afstand wisselt de winnaar per frame. renderOrder
          // hoger dan de spoel (0) én de wireframe (1) forceert het label altijd als laatste
          // (dus altijd zichtbaar) te tekenen. polygonOffset als extra depth-marge.
          labelMesh.renderOrder = 2;
          labelMatRef.polygonOffset = true;
          labelMatRef.polygonOffsetFactor = -1;
          labelMatRef.polygonOffsetUnits = -1;
          // Label leesbaar + donkergroen getint (neutrale witte basis, zelf-verlicht via
          // emissiveMap (eigen texture), in de shader naar luminantie gereduceerd en dan
          // met LABEL_TINT getint i.p.v. puur grijs) — meer een achtergrond-ding, minder
          // een fel wit sticker-vlak.
          labelMatRef.color = new THREE.Color(0xffffff);
          if (labelMatRef.map) {
            labelMatRef.emissiveMap = labelMatRef.map;
            labelMatRef.emissive = new THREE.Color(0xffffff);
            labelMatRef.emissiveIntensity = LABEL_EMISSIVE;
            // Anisotropic filtering (Vince: label flikkert nog steeds hard): een gedetailleerde
            // 2922x2000 textuur op een continu roterend/schalend vlak (scroll-gekoppelde spin +
            // zoom) kan zonder anisotropy zichtbaar shimmeren/poppen tussen mipmap-niveaus per
            // frame — een heel andere flicker-oorzaak dan depth-sort-instabiliteit.
            labelMatRef.map.anisotropy = renderer.capabilities.getMaxAnisotropy();
          }
          labelMatRef.onBeforeCompile = (shader) => {
            shader.uniforms.uLabelTint = { value: LABEL_TINT };
            const fB = shader.fragmentShader;
            shader.fragmentShader = shader.fragmentShader
              .replace('#include <common>', '#include <common>\nuniform vec3 uLabelTint;')
              // Bug gevonden + gefixt: dit zat voorheen na <color_fragment>, dat draait VOOR
              // <map_fragment> de textuur toepast — diffuseColor.rgb was daar dus nog gewoon
              // de vlakke witte basiskleur, nooit de echte labeltextuur (vandaar dat de tint
              // grauw/plat bleef ogen). Nu na <map_fragment>, waar diffuseColor.rgb het echte
              // texture-sample bevat.
              // Pixel-analyse van label_750g-175.png (Blender, 91500 samples): sterk tweeledig
              // — 78225 samples rond luminantie 0.14-0.19 (donkere achtergrond-plaat), 12845
              // rond 0.85-1.0 (tekst/logo), nauwelijks iets ertussenin. Drempel 0.45-0.65 zit
              // ruim in het lege midden, dus scherpe scheiding zonder randjes-artefacten.
              // Vince: alleen de tekst tonen, het label-vlak zelf verbergen (spoel-oppervlak
              // schemert door) — mix() geeft de tekst-pixels een punchy groen (floor voorkomt
              // zwart-crash bij tussenliggende randpixels), alpha maskeert de achtergrond naar
              // onzichtbaar. Emissive ook gemaskeerd (dubbele zekerheid, mocht alpha-blending
              // ooit anders lopen dan verwacht).
              .replace('#include <map_fragment>', '#include <map_fragment>\nfloat _labLum = dot(diffuseColor.rgb, vec3(0.299,0.587,0.114));\nfloat _labMask = smoothstep(0.45, 0.65, _labLum);\ndiffuseColor.rgb = mix(uLabelTint * 0.28, uLabelTint * 1.6, _labLum);\ndiffuseColor.a *= _labMask;')
              .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\nfloat _labELum = dot(totalEmissiveRadiance, vec3(0.299,0.587,0.114));\nfloat _labEMask = smoothstep(0.45, 0.65, _labELum);\ntotalEmissiveRadiance = mix(uLabelTint * 0.28, uLabelTint * 1.6, _labELum) * _labEMask;');
            if (shader.fragmentShader === fB) console.error('LABEL-TINT: shader-anchor mismatch');
          };
          labelMatRef.customProgramCacheKey = () => 'label-tint';
          labelMatRef.needsUpdate = true;
          await nextFrame();
        } else if (matName.toLowerCase().includes('spool')) {
          child.material = spoolMat;
          spoolMesh = child;
          stockSpoolParts.push(child); // bron voor de voorraad-kopieën
          ensurePlanarUV(child.geometry); // UV's voor de normal map (mesh heeft ze waarschijnlijk niet)
          await nextFrame();
          const spoolSharp = await makeFatLine(child.geometry, 0xCFEEDB, 1.8, false, 1.0, 55);
          child.add(spoolSharp.line);
          spoolWireframeOverlays.push(spoolSharp.mat);
        } else {
          // Het filament komt compact binnen (7,2 → 1,3 MB): gedeelde hoekpunten, zonder
          // normalen, en de Draco-compressie tekent de driehoekjes in een eigen volgorde.
          // Hier alles terugzetten, zodat de geometrie gelijk is aan het oude model.
          // Tussen de stappen even ruimte voor een tik (INP, 2026-11-03): samen kostte dit
          // bij 10x vertraagde CPU ~385 ms aan één stuk. Volgorde en uitkomst blijven gelijk.
          if (!child.geometry.attributes.normal) {
            const geo = child.geometry.toNonIndexed();
            await yieldToMain();
            const extra = child.userData;
            // 1. Oorspronkelijke tekenvolgorde. Het filament is licht doorzichtig, dus de
            //    volgorde bepaalt welke wikkelingen doorschemeren. De lijst geeft per driehoek
            //    de oorspronkelijke plek, als verschil met de vorige (zigzag-varint).
            if (extra.faceOrder) {
              const bytes = new Uint8Array(await gltf.parser.getDependency('bufferView', extra.faceOrder.bufferView));
              const src = geo.attributes.position.array, dst = new Float32Array(src.length);
              for (let f = 0, p = 0, at = 0; f < extra.faceOrder.count; f++) {
                let z = 0, shift = 0, b;
                do { b = bytes[p++]; z |= (b & 127) << shift; shift += 7; } while (b & 128);
                at += (z >>> 1) ^ -(z & 1);
                dst.set(src.subarray(f * 9, f * 9 + 9), at * 9);
              }
              geo.setAttribute('position', new THREE.BufferAttribute(dst, 3));
              await yieldToMain();
            }
            // 2. Platte facet-normalen terugrekenen: zelfde facet-look en wireframe als voorheen.
            geo.computeVertexNormals();
            await yieldToMain();
            // 3. ~11k dunne driehoekjes in de groeven tussen de wikkelingen krijgen hun
            //    oorspronkelijke richting terug. Per regel: face-index (Uint32) + normaal (Int8 x3).
            if (extra.normalPatch) {
              const buf = await gltf.parser.getDependency('bufferView', extra.normalPatch.bufferView);
              const faces = new Uint32Array(buf, 0, extra.normalPatch.count);
              const dirs = new Int8Array(buf, extra.normalPatch.count * 4, extra.normalPatch.count * 3);
              const nrm = geo.attributes.normal.array;
              for (let k = 0; k < extra.normalPatch.count; k++) {
                const x = dirs[3 * k], y = dirs[3 * k + 1], z = dirs[3 * k + 2];
                const len = Math.hypot(x, y, z) || 1;
                for (let c = 0; c < 3; c++) {
                  const o = (faces[k] * 3 + c) * 3;
                  nrm[o] = x / len; nrm[o + 1] = y / len; nrm[o + 2] = z / len;
                }
              }
              await yieldToMain();
            }
            child.geometry = geo;
          }
          child.material = filamentMat;
          // Meet de echte wind-radius-envelope (axle = lokaal X) zodat wind=0 exact tot de
          // kern clipt en wind=1 100% toont — geen gegokte 25/95.
          { const arr = child.geometry.attributes.position.array;
            for (let v = 0; v < arr.length; v += 3) {
              const r = Math.hypot(arr[v + 1], arr[v + 2]); // y,z
              if (r < filRMin) filRMin = r;
              if (r > filRMax) filRMax = r;
            } }
          stockFilParts.push(child); // bron voor de voorraad-kopieën
          toriCount++;
          if (i % 5 === 0) await nextFrame();
          // Draadmodel van het filament alleen bouwen als een scrollstap het laat zien. filWire staat
          // in alle STOPS op 0, en de EdgesGeometry over 331k driehoekjes was het grootste blok
          // rekenwerk bij het laden (~1,3 s op een middenklasse telefoon, knoppen reageerden niet).
          if (STOPS.some(st => (st.filWire || 0) > 0)) {
            const filSharp = await makeFatLine(child.geometry, 0x63D992, 2.0, false, 1.0, 48);
            child.add(filSharp.line);
            filamentWireframeOverlays.push(filSharp.mat);
          }
        }
      }
      // Gemeten radius-envelope toepassen (fallback blijft als de meting leeg was).
      if (filRMax > filRMin) { WIND_R_INNER = filRMin; WIND_R_OUTER = filRMax + 0.5; }

      step = 'hierarchy';
      await nextFrame();
      wrapper = new THREE.Group();
      spinner = new THREE.Group();
      scene.add(wrapper);
      wrapper.add(spinner);
      spinner.add(model);
      model.updateMatrixWorld(true);

      if (spoolMesh) {
        const spoolWorld = new THREE.Vector3();
        spoolMesh.getWorldPosition(spoolWorld);
        model.position.sub(spoolWorld);
        model.updateMatrixWorld(true);
      }

      step = 'camera';
      await nextFrame();
      wrapper.rotation.x = -Math.PI / 2;
      wrapper.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(wrapper);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * Math.PI / 180;
      baseCameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.6;

      // Grote 3D-ringen rond de spoel — dunne lichte kern + zachte halo (pseudo-blur). Draaien mee via spinner.
      ringGroup = new THREE.Group();
      const ringCoreMat = new THREE.MeshBasicMaterial({ color: 0xE4EDE6, transparent: true, opacity: 0.15 });
      const ringHaloMat = new THREE.MeshBasicMaterial({ color: 0xE4EDE6, transparent: true, opacity: 0.03, depthWrite: false });
      ringMatRefs = [ringCoreMat, ringHaloMat];
      [
        { r: maxDim * 0.80, rx: 0.5, ry: 0.0, rz: 0.15 },
        { r: maxDim * 1.05, rx: 1.3, ry: 0.4, rz: 0.0 },
      ].forEach(cfg => {
        const core = new THREE.Mesh(new THREE.TorusGeometry(cfg.r, maxDim * 0.0012, 8, 140), ringCoreMat);
        const halo = new THREE.Mesh(new THREE.TorusGeometry(cfg.r, maxDim * 0.006, 8, 140), ringHaloMat);
        core.rotation.set(cfg.rx, cfg.ry, cfg.rz);
        halo.rotation.set(cfg.rx, cfg.ry, cfg.rz);
        ringGroup.add(core, halo);
      });
      spinner.add(ringGroup);

      // Gedetailleerde voorraad-instances bouwen (enige toevoeging t.o.v. origineel)
      buildStock();

      statsEl.textContent =
        `Total meshes: ${totalMeshes}\n` +
        `Spool: ${spoolMesh ? 'yes' : 'no'}\n` +
        `Tori: ${toriCount}`;

      // Pre-warm — verstopt achter de 0-100% loader. In WebGL compileren shaders
      // en uploaden GPU-buffers pas bij de éérste render van een materiaal. Zonder
      // warm-up krijg je dus een compile-hitch precies wanneer iets voor het eerst
      // in beeld scrollt (wireframe in Engineering, filament-fade, bloom-pass).
      // Hier doen we die eerste-keer-kosten alvast, vóór hideLoader.
      step = 'prewarm';
      // Balk telt door van 90 → 100 tijdens de pre-warm i.p.v. te bevriezen op 100:
      // trage creep naar 99 terwijl de shaders compileren, snap naar 100 als klaar.
      setLoaderProgress(90);
      let warmPct = 90;
      let warming = true;
      (function creep() {
        if (!warming) return;
        warmPct = Math.min(99, warmPct + 0.2);
        setLoaderProgress(Math.round(warmPct));
        requestAnimationFrame(creep);
      })();
      await nextFrame();
      try {
        // Camera op de hero-stand zodat de warm-up de spoel echt rendert.
        const s0 = getStopState(0);
        const d0 = baseCameraZ * s0.camDist;
        camera.position.set(TARGET_OFFSET_X + s0.camDirX * d0, s0.targetY + s0.camDirY * d0, s0.camDirZ * d0);
        camera.lookAt(TARGET_OFFSET_X, s0.targetY, 0);

        // Forceer alle wireframes zichtbaar zodat compileAsync óók hun LineMaterial
        // pakt (anders compileren ze pas wanneer ze in beeld scrollen). De cull in
        // animate zet ze daarna meteen weer goed.
        [...spoolWireframeOverlays, ...filamentWireframeOverlays].forEach(m => {
          if (m.userData.line) m.userData.line.visible = true;
        });
        // Voorraad-instances ook even zichtbaar zodat hun shaders nu compileren.
        if (stockSpoolInst) stockSpoolInst.visible = true;
        if (stockFilInst) stockFilInst.visible = true;
        filWindUniforms.uWindR.value = WIND_R_OUTER; // warm óók de niet-gediscarde (volle) staat

        tmark('warmStart');
        if (renderer.compileAsync) {
          await renderer.compileAsync(scene, camera);
        } else {
          renderer.compile(scene, camera);
        }

        // Warm de post-processing (bloom/output zitten niet in de scene, dus
        // compileAsync pakt ze niet) + beide filament-transparant-permutaties —
        // de transparent-toggle mid-scroll zou anders een recompile-hitch geven.
        composer.render();
        const prevT = filamentMat.transparent;
        filamentMat.transparent = !prevT; filamentMat.needsUpdate = true;
        composer.render();
        filamentMat.transparent = prevT; filamentMat.needsUpdate = true;
        composer.render();
      } catch (warmErr) {
        console.warn('prewarm overgeslagen:', warmErr);
      }
      tmark('warmEnd');
      warming = false;
      setLoaderProgress(100);

      requestAnimationFrame(() => requestAnimationFrame(hideLoader));
    } catch (e) {
      console.error('onLoad error at', step, e);
      const msg = (e.message || String(e)).slice(0, 40);
      setLoaderIndeterminate(`${step}: ${msg}`);
    }
  },
  (xhr) => {
    if (xhr.total) {
      // Plafond op 100: de CDN comprimeert het model onderweg, dan is total de gecomprimeerde
      // grootte en telt loaded de uitgepakte bytes (1,6 MB tegen 1,4 MB gaf 105% op de balk).
      const pct = Math.min(100, Math.round(xhr.loaded / xhr.total * 100));
      statsEl.textContent = `Loading model... ${pct}%`;
      // Download vult 0→90; de laatste 90→100 is voor de pre-warm (zie onLoad),
      // zodat de balk doortelt i.p.v. op 100 te bevriezen tijdens de compile.
      setLoaderProgress(Math.round(pct * 0.9));
    } else {
      const mb = (xhr.loaded / 1048576).toFixed(1);
      setLoaderIndeterminate(`${mb} MB`);
    }
  },
  (error) => {
    console.error('GLB load error:', error);
    const msg = (error.message || error.toString() || 'unknown').slice(0, 40);
    statsEl.textContent = `Error: ${msg}`;
    setLoaderIndeterminate(msg);
  }
);

// Animate loop: scroll-driven journey
// Token-lerp losgekoppeld van rAF: ook bij browser-throttled rAF (bv. tijdens
// MeshOpt-decode of bij background-tab) updaten de tokens netjes op het scroll-event.
//
// Belangrijk: de vlakke achtergrond komt uit scene.background = bgTexture (de
// blueprint/design-canvas, zie drawBlueprintBg). Die krijgt de ACES-gecompenseerde
// kleur (SCENE_BG_*) zodat 'ie ná tonemapping exact gelijk rendert aan de pure
// CSS --bg die hieronder mee-lerpt voor body/loader/linkerkolom. (Een eerdere
// generatie had scene.background op null met de kleur uit CSS — dat comment
// beschreef precies de blanco-canvas-bug en is 2026-07-17 gecorrigeerd.)
const themeMeta = document.querySelector('meta[name="theme-color"]');
let _bgMobileLast = -1; // mobiele throttle voor de dure vol-scherm bg-grid textuur-upload
function updateColorTokens() {
  if (Math.abs(scrollProgress - lastBgUpdate) <= 0.002) return;
  // --bg (CSS) lerpte ooit Pine → Moss binnen het 3D-deel (p=0..0.75); bleef daarna Moss
  // staan tot de Paper-overlay het overnam. Eerst al UIT op mobiel (Vince: "alles wordt
  // steeds grijzer, ik wil dat hij de hele tijd die donkergroen blijft"), en nu ook op
  // desktop — Vince wilde de linker kolom net zo constant donkergroen als op mobiel, niet
  // een gradient naar grijzig. bgT/textT dus universeel vast op 0 = pure Pine + Paper-
  // tekst de hele journey door, geen lerp naar Moss. Beide samen bevriezen (niet alleen bg)
  // voorkomt dat de tekst later alsnog donker zou worden tegen een bg die niet meer licht
  // wordt — dat zou onleesbaar zijn.
  const bgT = 0;
  _bgTmp.copy(BG_DARK).lerp(BG_MID, bgT);
  const textT = 0;
  _inkTmp.copy(INK_LIGHT).lerp(INK_DARK, textT);
  _muteTmp.copy(MUTE_SAGE).lerp(MUTE_FOREST, textT);
  // scene.background krijgt de ACES-gecompenseerde lerp, zodat 'ie ná tonemapping exact
  // gelijk rendert aan de pure CSS --bg (geen donkerder/grauwer canvas meer).
  // De bg-textuur-upload (vol-scherm canvas → GPU) is duur en gebeurde bijna elke
  // scroll-frame. Mobiel: coarse throttle op bgT. Desktop: needsUpdate alleen als
  // drawBlueprintBg écht hertekende (guard-return) — met bgT vast op 0 is de hex
  // constant en zou elke scroll-upload een bit-identieke multi-megapixel texture
  // opnieuw naar de GPU sturen (review 2026-07-17).
  if (!MOBILE_INIT || Math.abs(bgT - _bgMobileLast) > 0.1) {
    _bgMobileLast = bgT;
    _sceneBgTmp.copy(SCENE_BG_DARK).lerp(SCENE_BG_MID, bgT);
    if (drawBlueprintBg('#' + _sceneBgTmp.getHexString())) bgTexture.needsUpdate = true;
  }
  const root = document.documentElement.style;
  root.setProperty('--bg', '#' + _bgTmp.getHexString());
  root.setProperty('--pine', '#' + _inkTmp.getHexString());
  root.setProperty('--pine-mute', '#' + _muteTmp.getHexString());

  // iOS-chrome (status/URL-balk) mee laten tinten: donker in de journey, Paper in
  // de catalogus, zodat 'ie niet als afwijkende balk opvalt (o.a. in het laadscherm).
  const tc = scrollProgress >= 0.95 ? '#F4F5F0' : '#1B2A1F';
  if (themeMeta && themeMeta.getAttribute('content') !== tc) themeMeta.setAttribute('content', tc);

  lastBgUpdate = scrollProgress;
}

// Initieer tokens op page-load zodat een directe deep-link (#parking) ook klopt
updateColorTokens();
scrollTarget.addEventListener('scroll', rafThrottle(updateColorTokens), { passive: true });

// === Drumroll: dramatische climax in de fade-zone vóór de catalogus ===
// "Inademen → opbouwen → loslaten": spin-up + groene emissive-charge + bloom-flare
// die op de piek wegwit richting de Paper-catalogus. Tunebaar via deze constanten.
// Desktop: eind-gekoppeld (p -> 1.0), piek dus op (DR_START+1)/2 = 0.815 — Vince wil 'm
// daar expliciet vlak vóór het einde van de journey ("het idee was juist dat die aangaat
// net voordat je voorbij de hele animatie bent"), en dat bleef zo na alle mobile-pogingen.
// Mobiel: .parking-band begint het canvas van onderaf te dekken vanaf scrollProgress ≈
// 0.55 (journeyMax/viewportHeight-verhouding op een 375x812-viewport) — ruim vóór de
// desktop-piek van 0.815. Losgeprobeerd via model-herpositionering (rise/pin/scroll-lock,
// alle drie afgekeurd door Vince); uiteindelijk de simpelste hendel: piek eerder zetten.
// Twee rondes te vroeg (0.08->1.0, toen 0.35->0.65) — Vince bleef "te snel/te vroeg" melden.
// Venster nu kort en vlak vóór het dekpunt (0.45->0.55, midden nog steeds 0.5). Daarnaast
// bleef 'ie na de piek weer UIT gaan (symmetrische sin-hump, zie flare hieronder) — Vince
// wil dat 'ie AANBLIJFT. Op mobiel houdt flare daarom de piek vast i.p.v. terug te zakken.
const DR_START = MOBILE_INIT ? 0.45 : 0.63; // scrollProgress waar de drumroll begint
const DR_END = MOBILE_INIT ? 0.55 : 1.0;    // scrollProgress waar de opbouw voltooid is (mobiel: piek/vol op 0.5, blijft daarna aan)
const DR_SPIN = Math.PI * 1;   // extra spin-versnelling (rev-up), accelererend (drum²) — rustige halve slag, geen wilde tol
const DR_EMISSIVE = 6.0;      // Vince: nog feller (was 4.5, daarvoor 3)
const DR_BLOOM = 1.4;         // extra bloom-strength op de piek (de flare/wegwit) — Vince: nog feller (was 1.0, daarvoor 0.65)
const DR_SCALE = 0;           // alt3: geen scale-surge op het eind (rustiger)
const BLOOM_BASE = 0.18;      // bloom-strength buiten de drumroll (= UnrealBloomPass-init)

// === Portrait/mobiel framing ===
// Op een staand scherm staat de tekst (CSS) bovenaan de step; de spoel wordt
// gecentreerd, uitgezoomd (past smal) en naar de onderhelft geduwd zodat 'ie de
// tekst niet overlapt. De skip-knop (vast onderaan-midden) zweeft dan over de
// spoel, niet over de tekst. Tunebaar.
const MOBILE_ZOOM = 1.42;         // camDist-multiplier in portrait (hoger = kleiner/verder)
const MOBILE_LIFT_FACTOR = -0.14; // verticale offset = baseCameraZ * factor (negatief = omlaag)
// Face-on camera (= de actieve journey-camera, ankert op _stockCenter) is portrait-blind:
// de oude MOBILE_ZOOM/lift voeden alleen het legacy _journeyCamPos-pad. Daarom apart inhaken:
const MOBILE_FACE_ZOOM = 0.982;   // faceDist-multiplier in portrait (was 1.08, /1.10 voor Vince's "model 10% groter" — apparent size ∝ 1/faceDist, dus kleinere multiplier = dichterbij = groter)
const MOBILE_FACE_RIGHTX = 0;     // rightX in portrait: 0 = spoel horizontaal gecentreerd (tekst staat erboven via CSS)
const MOBILE_FACE_K = 2.5;        // vaste faceK in portrait (overschrijft per-stop faceK, bv. hero 1.1) → elk station even groot
const MOBILE_TXT_GAP = 0.03;      // gewenste marge (scherm-fractie) tussen hero-tekst-onderkant en model-bovenrand

// Mobiele model-plaatsing is announcement-aware: het model hangt net ONDER de hero-tekst,
// en de announcement-bar verschuift die tekst (mét balk ~80%, zonder ~67%). We meten de
// hero-CTA-onderkant als scherm-fractie op scroll≈0 en plaatsen het model daar net onder
// (zie projectie-formule in animate()). Update op load/resize + elke 500ms (vangt het
// wegklikken van de announcement-bar op).
let _heroTxtBotFrac = 0.8;
function measureHeroTxtBot() {
  if (getScrollY() > 5) return;                   // alleen in rust meten; tijdens scroll schuift de CTA mee
  const el = document.querySelector('.step[data-step="0"] .cta-row');
  if (!el) return;
  const b = el.getBoundingClientRect();
  // Fractie van de CANVAS-hoogte, niet van innerHeight: de projectie in animate() rekent
  // in canvas-hoogte, en op een telefoon is de canvas zo hoog als het hele scherm
  // (--full-h, zie applyViewportSize). Met innerHeight stond de spoel op de iPhone zo'n
  // 105pt te laag, en sinds iOS 26 knipt Safari dat onderste stuk ook nog af achter de
  // adresbalk (Vince 2026-10-12, gemeten: canvas 956, inner 796). Op desktop zijn beide
  // gelijk, daar verandert niets.
  const H = canvas.clientHeight || window.innerHeight;
  if (b.height > 0) _heroTxtBotFrac = Math.min(0.92, b.bottom / H);
}
measureHeroTxtBot();
setInterval(measureHeroTxtBot, 500);

let finalRendered = false;
// Wheel-spin op scroll (rolt alleen als je scrollt, niet continu): aantal omwentelingen over
// de journey. Vanaf station 3 (p>0.667) komen extra turns bij zodat 'ie daar iets rapper rolt.
const SPIN_TURNS_BASE = 1.2;   // omwentelingen over de hele journey (station 1 t/m 3)
const SPIN_TURNS_FAST = 0.6;   // extra omwentelingen die vanaf station 3 bijkomen
// Reduced-motion (inc 4): respecteer prefers-reduced-motion. De spoel bevriest dan op
// één vaste pose (RM_POSE) zonder spin, idle-bob/sway, intro-draai of muis-parallax. De
// pagina-kleuren en native scroll blijven werken (dat is geen vestibulaire motion).
const REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const RM_POSE = 0.5; // bevroren scroll-pose voor de statische spoel (gevuld, gesetteld)
let pageReleased = false; // zie de pagehide-afhandeling onder animate()
// Tik-voorrang (INP, 2026-11-03). Sinds de themawissel is de reactietijd op een tik op
// telefoons van ~150 naar ~520 ms gegaan (Chrome-gebruikers, p75), en de doorlopende
// render hieronder is daar de grootste post van: een tik wacht eerst op het lopende 3D-frame
// en daarna op nog een. Daarom tussen het neerzetten van de vinger en net na het loslaten
// geen nieuw frame; de reactie op de tik (knop, menu, link) gaat dan voor. De stand rekent
// gewoon door, alleen het tekenen slaat over, dus daarna staat de spoel meteen goed. Wordt
// de tik een scroll, dan geeft de browser pointercancel en tekent alles direct weer, zodat
// de scroll-animatie niet stokt. Gemeten bij 10x CPU: tik 64 naar 32 ms. Naslag in
// Creaties/SHOPIFY-MIGRATION.md, "INP op mobiel".
// Alleen voor vinger en pen: met de muis is de reactietijd al goed (desktop 118/118 goed),
// en daar slepen mensen de scroll-markering. Beweegt de vinger meer dan 10px zonder dat de
// browser zelf scrolt (de scroll-markering slepen, een rij opzij trekken), dan tekent alles
// ook direct weer.
let tapHoldUntil = 0, tapX = 0, tapY = 0;
window.addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'mouse') return;
  tapX = e.clientX; tapY = e.clientY;
  tapHoldUntil = performance.now() + 300;
}, { capture: true, passive: true });
const holdAfterTap = () => { const now = performance.now(); if (tapHoldUntil > now) tapHoldUntil = now + 120; };
window.addEventListener('pointerup', holdAfterTap, { capture: true, passive: true });
window.addEventListener('click', holdAfterTap, { capture: true, passive: true });
window.addEventListener('pointercancel', () => { tapHoldUntil = 0; }, { capture: true, passive: true });
window.addEventListener('pointermove', (e) => {
  if (tapHoldUntil && Math.abs(e.clientX - tapX) + Math.abs(e.clientY - tapY) > 10) tapHoldUntil = 0;
}, { capture: true, passive: true });
// Stilstaan op telefoons (2026-11-03, Vince: "Ja, doe dat"). Op een telefoon tekent de scène
// alleen nog als er iets verandert: scroll, de intro, de schermgrootte of donkere modus. Daarvoor
// zat de GPU elke frame vol (DPR tot 3, 4x MSAA, bloom op volle resolutie), ook als niemand
// scrolde, en een tik moest daarop wachten. Een frame overslaan laat het vorige beeld staan. De
// zweefbeweging staat op een telefoon daarom uit; die was daar maar een paar pixels in ruim 2 s.
// Desktop en tablet tekenen doorlopend, zoals voorheen.
const TOUCH_DEVICE = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
let stillKey = '';
const wakeScene = () => { stillKey = ''; };
document.addEventListener('visibilitychange', wakeScene);
window.addEventListener('pageshow', wakeScene);
function animate(time) {
  if (pageReleased) return;
  requestAnimationFrame(animate);
  const phoneStill = TOUCH_DEVICE && window.innerWidth <= 768;

  // Lenis aandrijven met dezelfde rAF-clock; update't de smooth scroll-positie.
  if (lenis && time) lenis.raf(time);

  // scrollProgress elke frame opnieuw uit de scroll-positie lezen i.p.v. te leunen op
  // het 'scroll'-event: iOS levert scroll-events grof/gebatcht aan tijdens touch-
  // scroll, waardoor de 3D in brokken sprong ("weinig frames"). getScrollY() is
  // altijd actueel, dus zo volgt de animatie de scroll vloeiend per frame.
  updateScroll();

  // Animatie klaar (catalogus): het model is onzichtbaar onder de parking-band
  // CSS-laag. Render dan nog één keer de eindstaat en daarna niets meer — anders
  // blijft de zware pijplijn (MSAA 8x + bloom + 167K verts) 60×/sec doordraaien
  // en stottert de native catalogus-scroll mee ("traag/met moeite"). Resumed
  // zodra je terug omhoog de journey in scrollt.
  if (scrollProgress >= 1) {
    if (!finalRendered) {
      updateColorTokens();
      composer.render();
      finalRendered = true;
    }
    return;
  }
  finalRendered = false;

  // Bg-lerp ook elke frame meedraaien (smooth tussen scroll-events in)
  updateColorTokens();

  if (wrapper && spinner && baseCameraZ > 0) {
    // Mobiel: STOPS-interpolatie bevriest op Research (p=0.667) i.p.v. doorlopen naar
    // Parking's fade-out (filOpacity/spoolOpacity → 0, kleur → naturel). Vince: "de glow
    // gaat aan en uit" — de flare-boost hield al stand (zie flare hieronder), maar de
    // onderliggende opacity bleef gewoon richting Parking faden, dus het gloed-effect
    // verdween alsnog via een heel ander kanaal (transparant materiaal, geen pixels om
    // te bloomen). Op mobiel mag het model uitsluitend verdwijnen doordat .parking-band
    // 'm van onderaf bedekt — niet doordat het zelf transparant wordt. scrollProgress
    // zelf blijft ongeclampt voor flare/spin/rise elders in deze functie.
    const _stopProgress = MOBILE_INIT ? Math.min(scrollProgress, 0.667) : scrollProgress;
    const s = getStopState(REDUCED_MOTION ? RM_POSE : _stopProgress);

    // Voorraad-timing: de voorraad vormt zich 0.58→0.74 en staat dus VOL in
    // beeld als je op het laatste station (Products, 0.75) landt; houdt 0.74→0.93
    // en faadt 0.93→1.0 mee naar de catalogus. `calm` dempt het idle-leven zodat
    // de hero rustig "geparkeerd" oogt terwijl de voorraad opkomt.
    const sstep = (a, b, x) => { const u = Math.min(1, Math.max(0, (x - a) / (b - a))); return u * u * (3 - 2 * u); };
    const rackIn = 0; // alt3: voorraadkast geskipt — geen kast, dus geen terug-draai naar de kopse kant
    const rackOut = sstep(0.93, 1.0, scrollProgress);
    const rackP = rackIn * (1 - rackOut);
    const calm = 1 - rackIn * 0.9;

    // Intro-reveal bij page-load: model zwiept groter in beeld + draait harder uit
    let introT = 1;
    if (introStart !== null && time) {
      // Ook naar beneden clampen: als de rAF-klok achterloopt op introStart
      // (o.a. onder headless virtual-time) wordt introT negatief en explodeert
      // introScale → wrapper.scale. Echte browsers hebben er geen last van.
      introT = Math.min(1, Math.max(0, (time - introStart) / 1700));
      introT = 1 - Math.pow(1 - introT, 3); // easeOutCubic
    }
    const introSpin = REDUCED_MOTION ? 0 : (1 - introT) * -2.2;  // hardere inkom-draai
    const introScale = REDUCED_MOTION ? 1 : 0.7 + introT * 0.3;  // groeit meer in beeld (70% → 100%)

    // Idle hover: continu subtiel leven (zweeft + wiebelt + drift), ook in stilstand.
    // Amplitudes nu 2x verder omhoog (was 8/7, daarvoor 5/4): Vince zag 'm nog steeds
    // niet zweven. Dit is onafhankelijk van de mouse-parallax hieronder (die stuurt
    // spinner.rotation, niet wrapper.position) — het één overschrijft het ander niet,
    // maar bij een stilstaande muis moet dit nu ondubbelzinnig zichtbaar zijn.
    // Op een telefoon uit (phoneStill): zie "Stilstaan op telefoons" boven animate().
    const t = (time || 0) * 0.001;
    const noIdle = REDUCED_MOTION || phoneStill;
    const hoverY = noIdle ? 0 : Math.sin(t * 0.9) * 14;
    const swayX = noIdle ? 0 : Math.sin(t * 0.7) * 0.025;
    const swayZ = noIdle ? 0 : Math.cos(t * 0.6) * 0.02;
    // Vince: model mag zachtjes heen en weer bewegen op z'n plek. Eigen (tragere)
    // periode + fase dan hoverY, anders lijkt het één mechanische cirkelbeweging
    // i.p.v. los, organisch leven.
    const driftX = noIdle ? 0 : Math.sin(t * 0.5 + 2.1) * 13;

    // Mouse-parallax: smooth-lerp naar de cursor-positie. Soft easing zodat
    // de spool 'leeft' zonder de scroll-narratief te overrulen.
    mouseSmoothX += REDUCED_MOTION ? 0 : (mouseTargetX - mouseSmoothX) * 0.06;
    mouseSmoothY += REDUCED_MOTION ? 0 : (mouseTargetY - mouseSmoothY) * 0.06;

    // Drumroll-curve in de fade-zone (p DR_START → DR_END): bouwt op naar een piek.
    // Desktop: lost daarna weer los (symmetrische sin-hump, zoals altijd). Mobiel: Vince
    // wil dat 'ie AANBLIJFT i.p.v. weer uitgaat — drum houdt zichzelf al vast op 1 voorbij
    // DR_END, dus daar alleen de stijgende helft van de sinus gebruiken (0→top) en die
    // vasthouden i.p.v.'m door te laten zakken naar 0.
    const drum = Math.min(1, Math.max(0, (scrollProgress - DR_START) / (DR_END - DR_START)));
    const flare = MOBILE_INIT ? Math.sin(Math.min(drum, 0.5) * Math.PI) : Math.sin(drum * Math.PI);

    // Portrait/mobiel: spoel centreren + naar de onderhelft duwen (tekst staat
    // bovenaan via CSS) en uitzoomen zodat 'ie smal past. Desktop = ongewijzigd.
    // Puur op breedte (geen hoogte/breedte-verhouding): CSS schakelt #scene tussen de
    // rechter-helft-layout en de volle-breedte-layout ook uitsluitend op breedte
    // (min-width/max-width: 768px), dus de camera-framing moet exact diezelfde ene
    // grens gebruiken. Eerdere versie had ook nog een hoogte>breedte-eis erbij — leek
    // logisch ("portrait"), maar introduceerde een NIEUWE mismatch: een brede-maar-
    // korte smalle desktop-browser (breedte ≤768, hoogte niet > breedte) kreeg dan
    // desktop-framing terwijl CSS al op de volle-breedte mobiele layout stond, en het
    // model verdween opnieuw (Vince: "desktop smal verdwijnt de CGI"). Er is geen
    // enkele reden om af te wijken van de CSS-breakpoint, dus nu 1-op-1 dezelfde check.
    const portrait = window.innerWidth <= 768;
    const zoom = portrait ? MOBILE_ZOOM : 1;
    const lift = portrait ? baseCameraZ * MOBILE_LIFT_FACTOR : 0;

    // Wrapper position + scale (+ idle bob/drift × calm + intro scale-in + drumroll-surge)
    wrapper.position.x = (portrait ? 0 : s.posX) + TARGET_OFFSET_X + driftX * calm;
    wrapper.position.y = hoverY * calm + lift;
    wrapper.scale.setScalar(s.scale * introScale * (1 + flare * DR_SCALE));

    // Spinner: scroll-gekoppelde as-spin via rotation.y (= flens-as), de hele
    // journey én de kast door — net als de rest van de pagina, niets draait
    // vanzelf. In de kast blijft de kast rechtop: rotation.x/z faden naar 0 (geen
    // tilt/roll), maar rotation.y (de as-spin) loopt gewoon met de scroll mee.
    // Omdat rotation.y de flens-as is, kantelt de grid daar niet van.
    // Wheel-spin gekoppeld aan scroll (rolt alleen als je scrollt): base-turns over de hele
    // journey + extra turns vanaf station 3 (p>0.667) zodat 'ie daar iets rapper rolt.
    const _spinTurns = REDUCED_MOTION ? 0 : (SPIN_TURNS_BASE * scrollProgress + SPIN_TURNS_FAST * Math.max(0, scrollProgress - 0.667));
    const journeySpinY = _spinTurns * Math.PI * 2 + introSpin + mouseSmoothX * 0.06;
    spinner.rotation.y = journeySpinY;
    spinner.rotation.x = (1 - rackIn) * (s.tiltX + swayX + mouseSmoothY * 0.04);
    spinner.rotation.z = (1 - rackIn) * (s.tiltZ + swayZ);

    // Ringen: eigen trage rotatie bovenop de spinner-spin → 3D orbital tumble.
    // Op portrait/mobiel verborgen + uitfaden zodra de voorraad opkomt (ze horen
    // niet thuis in de strakke voorraad-scène).
    if (ringGroup) {
      ringGroup.visible = !portrait && rackIn < 0.999;
      ringGroup.rotation.z = t * 0.15;
    }
    // Ringen faden mee weg in de laatste stap én expliciet weg tegen de aankomst.
    if (ringMatRefs) {
      const ringFade = s.filOpacity * (1 - rackIn);
      ringMatRefs[0].opacity = 0.15 * ringFade;
      ringMatRefs[1].opacity = 0.03 * ringFade;
    }

    // Camera: per-stop direction + distance + target Y (portrait zoomt uit)
    const dist = baseCameraZ * s.camDist * zoom;
    _journeyCamPos.set(TARGET_OFFSET_X + s.camDirX * dist, s.targetY + s.camDirY * dist, s.camDirZ * dist);
    // Kopse kant vanaf page load: het label zit op de flens, dus we kijken de hele
    // journey tegen de kopse kant aan (label meteen zichtbaar, geen lelijke "H"-zijkant
    // en geen draai). De rol vult zich face-on; in de kast zoomt 'ie uit naar de rij
    // gelabelde einden via rackIn.
    if (spoolMesh) {
      model.updateMatrixWorld(true);
      _stockAxis.copy(STOCK_AXIS_LOCAL).transformDirection(spoolMesh.matrixWorld).normalize();
      spoolMesh.getWorldPosition(_stockCenter);
      const sgn = _tmpV.copy(_journeyCamPos).sub(_stockCenter).dot(_stockAxis) >= 0 ? 1 : -1;
      _stockAxis.multiplyScalar(sgn);
      _stockRight.crossVectors(_stockAxis, STOCK_UP);
      if (_stockRight.lengthSq() < 1e-6) _stockRight.set(1, 0, 0);
      _stockRight.normalize();
      _stockUp.crossVectors(_stockRight, _stockAxis).normalize();
      const flangeF = STOCK_FLANGE_DIAM * wrapper.scale.x;
      const pitchF = flangeF * STOCK.packK;
      // Afstand: per-stop faceK (Hero dichtbij/groot) → uitgezoomd voor de rij in de
      // kast. rackIn=1 reproduceert exact de oude kast-afstand (flange*cols*0.66),
      // onafhankelijk van faceK, dus de kast blijft gelijk.
      const effFaceK = portrait ? MOBILE_FACE_K : s.faceK;
      const faceDist = flangeF * (effFaceK + (STOCK.cols * 0.66 - effFaceK) * rackIn) * (portrait ? MOBILE_FACE_ZOOM : 1);
      // view 0 = kopse kant (camera langs de flens-as) → 1 = zijkant (loodrecht, langs
      // _stockRight). Constante hoeksnelheid (cos/sin) i.p.v. nlerp, zodat de draai
      // gelijkmatig loopt met de lineaire zoom/verschuiving → soepelere overgang st1→st2.
      const ang = s.view * Math.PI * 0.5;
      _camDir.copy(_stockAxis).multiplyScalar(Math.cos(ang)).addScaledVector(_stockRight, Math.sin(ang));
      // Hero-offsets (lowY/rightX) langs de CAMERA-schermassen i.p.v. wereld-assen, zodat
      // de verschuiving altijd horizontaal/verticaal-op-scherm blijft tijdens de draai
      // (geen diepte-wobble). Schuift camera ÉN kijk-doel samen → blijft face-on.
      _camRight.crossVectors(_camDir, STOCK_UP);
      if (_camRight.lengthSq() < 1e-6) _camRight.copy(_stockRight);
      _camRight.normalize();
      _camUp.crossVectors(_camRight, _camDir).normalize();
      // Mobiel: zet de model-bovenrand op 'target' (scherm-fractie vanaf boven), net onder
      // de gemeten hero-tekst — vast, geen rise/pin/lock. Uit de perspectief-projectie:
      // offUp(wereld) zo dat de top-rand = center − R op 'target' valt →
      // offUp = R + 2·faceDist·tan(fov/2)·(target − 0.5).
      let offUp;
      if (portrait) {
        const _tanHalf = Math.tan(camera.fov * Math.PI / 360);
        const _Rfl = STOCK_FLANGE_DIAM * 0.5 * wrapper.scale.x;
        const _target = Math.min(0.86, Math.max(0.5, _heroTxtBotFrac + MOBILE_TXT_GAP));
        offUp = _Rfl + 2 * faceDist * _tanHalf * (_target - 0.5);
      } else {
        offUp = s.lowY * flangeF;
      }
      const offRight = (portrait ? MOBILE_FACE_RIGHTX : 0) * flangeF; // twee-koloms: spoel gecentreerd in de rechter canvas-kolom (rightX vervalt op desktop)
      _faceCamPos.copy(_stockCenter).addScaledVector(_camDir, faceDist)
        .addScaledVector(_camUp, offUp).addScaledVector(_camRight, offRight);
      camera.position.copy(_faceCamPos);
      // gecentreerd in de journey; in de kast schuift de rij naar rechts (rackIn-offset,
      // langs _stockRight = de grid-horizontaal).
      _lookCur.copy(_stockCenter)
        .addScaledVector(_stockRight, pitchF * 0.32 * rackIn)
        .addScaledVector(_camUp, offUp).addScaledVector(_camRight, offRight);
      camera.lookAt(_lookCur);
      _faceDotDbg = _tmpV.set(0, 0, -1).applyQuaternion(camera.quaternion).dot(_stockAxis);
      _faceOnDbg = s.view;
    } else {
      camera.position.copy(_journeyCamPos);
      camera.lookAt(TARGET_OFFSET_X, s.targetY, 0);
    }

    // Per-stop material states (4-fase verhaal: solid → skeleton → solid-rebuilt → reveal)
    if (spoolMatRef) {
      spoolMatRef.opacity = s.spoolOpacity * RECEDE_OPACITY;
      spoolMatRef.color.copy(s.spoolCol);
      spoolMatRef.emissive.copy(s.spoolCol);   // lift-floor: eigen kleur × SPOOL_LIFT, uit de crush-zone
      spoolMatRef.emissiveIntensity = SPOOL_LIFT;
    }
    // Label faadt 1:1 met de spoel: zichtbaar bij Hero + Products-reveal, weg bij
    // Engineering/Research/parking.
    // Label: zichtbaar op de front, volledig weg zodra we voorbij de zijkant naar de achter-
    // kopse draaien. Opacity + emissive faden mee, én het mesh wordt hard verborgen (visible=
    // false) zodat er niks doorschemert door de half-transparante achterflens en niks flikkert.
    const _labelVis = 1 - sstep(0.55, 0.9, s.view);
    if (labelMatRef) {
      labelMatRef.opacity = s.spoolOpacity * _labelVis * RECEDE_OPACITY;
      labelMatRef.emissiveIntensity = LABEL_EMISSIVE * _labelVis * RECEDE_GLOW;
    }
    if (labelMesh) labelMesh.visible = _labelVis > 0.02;
    // Wireframe-cull: tienduizenden fat-line-segmenten met een dure shader. Sla de
    // hele draw over zodra ze onzichtbaar zijn (visible=false) — alleen Engineering
    // betaalt de volle kost, niet Hero/Research/Products/parking.
    spoolWireframeOverlays.forEach(mat => {
      const op = s.spoolWire * (mat.userData.maxOpacity ?? 1.0);
      mat.opacity = op;
      if (mat.userData.line) mat.userData.line.visible = op > 0.001;
    });
    filamentWireframeOverlays.forEach(mat => {
      // wireGate: de filament-wireframe mag nooit vóór de fill lopen (geen lijnen over
      // leegte) — opacity geschaald met hoe ver de coil gewikkeld is.
      const op = s.filWire * Math.min(1, s.wind * 1.15) * (mat.userData.maxOpacity ?? 1.0);
      mat.opacity = op;
      if (mat.userData.line) mat.userData.line.visible = op > 0.001;
    });
    if (filamentMatRef) {
      filamentMatRef.color.copy(s.filCol);
      // Emissive: normaal de neon-charge uit de Engineering-stap; in de drumroll een
      // dramatische groene oplading die de bloom-flare voedt (max van beide).
      filamentMatRef.emissiveIntensity = Math.max(s.filGlow * 2.8 * RECEDE_GLOW, flare * DR_EMISSIVE);
      filamentMatRef.opacity = s.filOpacity * RECEDE_OPACITY;
      // Wind-on: map wind 0..1 → axle-radius. wind=0 → onder de kern (alles geclipt =
      // lege haspel); wind=1 → R_OUTER (volle coil).
      filWindUniforms.uWindR.value = (WIND_R_INNER - 1) + (WIND_R_OUTER - (WIND_R_INNER - 1)) * s.wind;
      // Alleen transparent als opacity < 1 — voorkomt depth-sort doorkijk bij solid filament
      const needTransparent = s.filOpacity < 0.999;
      if (filamentMatRef.transparent !== needTransparent) {
        filamentMatRef.transparent = needTransparent;
        filamentMatRef.needsUpdate = true;
      }
    }

    // Bloom-flare: zwelt mee met de drumroll-piek en wast de groene charge wit;
    // buiten de drumroll terug naar de subtiele basis-glow.
    bloomPass.strength = BLOOM_BASE * RECEDE_GLOW + flare * DR_BLOOM;

    // ── Voorraad-aankomst: gedetailleerde rij identieke rollen, face-on ──────
    // De camera wordt langs de spoel-flens-as gericht → je kijkt tegen de kopse
    // kant aan, ingezoomd. De buren (instanced kopieën) verschijnen dicht op
    // elkaar in een grid naar rechts = de voorraad. Alleen aan het einde actief.
    if (stockSpoolInst || stockFilInst) {
      const show = rackP > 0.002;
      if (stockSpoolInst) stockSpoolInst.visible = show;
      if (stockFilInst) stockFilInst.visible = show;
      if (show) {
        model.updateMatrixWorld(true);
        _stockAxis.copy(STOCK_AXIS_LOCAL).transformDirection(spoolMesh.matrixWorld).normalize();
        spoolMesh.getWorldPosition(_stockCenter);
        const sgn = _tmpV.copy(camera.position).sub(_stockCenter).dot(_stockAxis) >= 0 ? 1 : -1;
        _stockAxis.multiplyScalar(sgn);
        _stockRight.crossVectors(_stockAxis, STOCK_UP);
        if (_stockRight.lengthSq() < 1e-6) _stockRight.set(1, 0, 0);
        _stockRight.normalize();
        _stockUp.crossVectors(_stockRight, _stockAxis).normalize();
        const wsc = wrapper.scale.x;
        const flange = STOCK_FLANGE_DIAM * wsc;
        const pitch = flange * STOCK.packK;
        // Camera (face-on langs de as + uitzoomen naar de rij) wordt nu centraal in de
        // hoofd-camera-sectie gezet via faceOn/rackIn; hier alleen nog instances plaatsen.
        // Instances plaatsen: kopie van de hero op offset langs right/up. De hero-
        // spin (journeySpinY via model.matrixWorld) zit al in elke kopie. Daar
        // bovenop krijgt elke buur een eigen vaste fase + een lichte scroll-
        // gekoppelde snelheidsvariatie → een wereld-space rotatie om _stockAxis
        // (= flens-as) door zijn EIGEN midden. Elke rol draait dus individueel
        // in-place om zijn eigen as, met de scroll mee (niets vanzelf). Het midden
        // beweegt niet, dus de kast blijft staan en niets tuimelt.
        const scrollSpin = scrollProgress * Math.PI * 2;
        let spin0 = 0;
        for (let i = 0; i < stockCount; i++) {
          const o = STOCK_OFF[i];
          _tmpOff.copy(_stockRight).multiplyScalar(o.ox * pitch).addScaledVector(_stockUp, o.oy * pitch);
          _stockM.makeTranslation(_tmpOff.x, _tmpOff.y, _tmpOff.z).multiply(model.matrixWorld);
          // delta t.o.v. de hero: vaste fase + scroll-gekoppelde snelheidsvariatie
          const speedMul = 1 + (((i * 7 + 3) % 11) / 11 - 0.5) * 0.2;   // determ. ±10%
          const delta = scrollSpin * (speedMul - 1) + (i + 1) * GOLDEN;
          if (i === 0) spin0 = journeySpinY + delta;
          _rollC.copy(_stockCenter).add(_tmpOff);                       // rol-midden (wereld)
          _spinRot.makeRotationAxis(_stockAxis, delta);
          _spinPivot.makeTranslation(_rollC.x, _rollC.y, _rollC.z).multiply(_spinRot);
          _spinRot.makeTranslation(-_rollC.x, -_rollC.y, -_rollC.z);
          _spinPivot.multiply(_spinRot);                                // T(c)·R·T(-c)
          _stockM.premultiply(_spinPivot);                              // spin om eigen midden
          if (stockSpoolInst) stockSpoolInst.setMatrixAt(i, _stockM);
          if (stockFilInst) stockFilInst.setMatrixAt(i, _stockM);
        }
        _stockSpin0 = spin0;
        if (stockSpoolInst) stockSpoolInst.instanceMatrix.needsUpdate = true;
        if (stockFilInst) stockFilInst.instanceMatrix.needsUpdate = true;
        if (stockSpoolMat) stockSpoolMat.opacity = rackP;
        if (stockFilMat) stockFilMat.opacity = rackP;
      }
    }

    // ?debug=1: face-on-check (dot van kijkrichting · flens-as ≈ ±1) + scherm-%.
    if (STORY_DEBUG && statsEl) {
      camera.updateMatrixWorld(true);
      const viewDir = _tmpV.set(0, 0, -1).applyQuaternion(camera.quaternion);
      const dot = (rackP > 0.002) ? viewDir.dot(_stockAxis).toFixed(2) : '-';
      const proj = (worldV) => { _rackProj.copy(worldV).project(camera); return ((_rackProj.x + 1) / 2 * 100).toFixed(0) + ',' + ((1 - _rackProj.y) / 2 * 100).toFixed(0); };
      // Buur rechts van de hero — moet op een VASTE schermplek blijven (geen roll)
      let neigh = '-';
      if (rackP > 0.002) {
        const pp = STOCK_FLANGE_DIAM * wrapper.scale.x * STOCK.packK;
        neigh = proj(_tmpV.copy(_stockCenter).addScaledVector(_stockRight, -pp));
      }
      const deg = (rad) => ((rad * 180 / Math.PI) % 360).toFixed(0);
      const _ctaEl = document.querySelector('.step[data-step="0"] .cta-row');
      const _txtBot = _ctaEl ? (_ctaEl.getBoundingClientRect().bottom / window.innerHeight * 100).toFixed(0) : '-';
      statsEl.textContent = 'txtBot=' + _txtBot + ' p=' + scrollProgress.toFixed(3) + ' rackP=' + rackP.toFixed(2)
        + ' faceDot=' + dot + ' hero=' + proj(_stockCenter)
        + ' topEdge=' + proj(_tmpV.copy(_stockCenter).addScaledVector(_camUp, STOCK_FLANGE_DIAM * 0.5 * wrapper.scale.x))
        + ' neighR=' + neigh
        + ' ySpin=' + deg(journeySpinY) + ' spin0=' + deg(_stockSpin0)
        + ' windR=' + WIND_R_INNER.toFixed(0) + '..' + WIND_R_OUTER.toFixed(0)
        + ' faceOn=' + _faceOnDbg.toFixed(2) + ' fDot=' + _faceDotDbg.toFixed(2)
        + ' count=' + stockCount;
    }
  }

  if (performance.now() < tapHoldUntil) return;
  // Telefoon na de intro: alleen tekenen als er iets veranderd is sinds het vorige beeld.
  if (phoneStill && introStart !== null && time - introStart > 2000) {
    const key = getScrollY().toFixed(1) + '|' + scrollProgress.toFixed(4) + '|' + mouseSmoothX.toFixed(4) + '|' + mouseSmoothY.toFixed(4)
      + '|' + canvas.width + 'x' + canvas.height + '|' + (document.documentElement.getAttribute('data-theme') || '');
    if (key === stillKey) return;
    stillKey = key;
  }
  composer.render();
}
animate();

// 3D-geheugen vrijgeven bij het verlaten van de pagina (2026-10-13). Een valutawissel laadt
// de voorpagina opnieuw. Zonder dit hield iOS Safari de oude scène vast (canvas op 3x, 4x
// MSAA-buffers, bloom) terwijl de nieuwe opbouwde, en na twee wissels schoot Safari het
// tabblad af ("Er heeft zich herhaaldelijk een probleem voorgedaan"). Alleen op touch-
// apparaten: op desktop is geheugen geen probleem en blijft terug-navigeren direct (bfcache).
// Komt de pagina na het opruimen toch uit de terug-cache, dan is de 3D-context weg en
// laden we opnieuw.
if (navigator.maxTouchPoints > 0) {
  window.addEventListener('pagehide', () => {
    pageReleased = true;
    try { composer.dispose(); } catch (e) {}
    try { bloomPass.dispose(); } catch (e) {}
    try { pmremGenerator.dispose(); } catch (e) {}
    try { renderer.dispose(); renderer.forceContextLoss(); } catch (e) {}
  });
  window.addEventListener('pageshow', (e) => { if (e.persisted && pageReleased) location.reload(); });
}

// iOS Safari vult een fixed 100vh niet tot de fysieke schermrand (achter de
// bottom-toolbar/home-indicator) — vandaar de halve dekking. We meten de
// volledige schermhoogte expliciet (screen-dimensie, oriëntatie-aware) en zetten
// 'm hard op de canvas (--full-h) én de render-buffer, zodat 'ie gegarandeerd
// full-bleed loopt zonder uitrekken. Op desktop = gewoon de window-hoogte.
// Bug (Vince: desktop-browser handmatig smal gemaakt, ~500px breed → leeg scherm, geen
// console-error): window.screen.width/height is het FYSIEKE MONITOR-formaat, niet het
// browservenster. Op een echte telefoon vallen die twee samen (de browser vult het hele
// scherm), dus de screenH-truc hieronder klopt daar. Op een handmatig versmald
// desktopvenster (mobile=true op basis van breedte, maar screen.height = volledige
// monitor, bv. 1440px) werd --full-h dus op de MONITOR-hoogte gezet i.p.v. de venster-
// hoogte — de canvas (position:fixed) werd daardoor veel hoger dan het venster, en het
// model kwam terecht in het deel dat onder de zichtbare fold viel (niet scrollbaar, want
// fixed). Nu alleen de screenH-correctie toepassen op een écht touch-apparaat.
const _isTouchDevice = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
let _lastVW = -1, _lastVH = -1;
function applyViewportSize() {
  const mobile = window.innerWidth <= 900;
  const portrait = window.innerHeight > window.innerWidth;
  const screenH = portrait
    ? Math.max(window.screen.width, window.screen.height)
    : Math.min(window.screen.width, window.screen.height);
  const w = canvas.clientWidth || window.innerWidth; // twee-koloms: canvas-CSS bepaalt de breedte (desktop rechter kolom, mobiel full-width)
  const h = (mobile && _isTouchDevice) ? Math.max(window.innerHeight, screenH) : window.innerHeight;
  // iOS vuurt tijdens scrollen continu resize-events af (toolbar klapt in/uit).
  // Onze h is geclampt op screenH (constant), dus de afmeting verandert dan niet —
  // skip het dure renderer/composer.setSize + style-recalc, anders herallokeren we
  // de GPU-buffers elke frame en hapert de animatie. Alleen bij échte size-change.
  // Eigen scrollvlak: het stuk onder de layout-viewport opnieuw meten (zie theme.liquid).
  if (OWN_SCROLL) document.documentElement.style.setProperty('--own-pad', Math.max(0, h - window.innerHeight) + 'px');
  if (w === _lastVW && h === _lastVH) return;
  _lastVW = w; _lastVH = h;
  document.documentElement.style.setProperty('--full-h', h + 'px');
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h, false); // false = canvas-CSS (var --full-h) bepaalt de display-grootte
  composer.setSize(w, h);
  allLineMaterials.forEach(mat => mat.resolution.set(w, h));
  sizeBgCanvas();              // blueprint-grid-texture mee herschalen (square cellen)
  // WEGGOOIEN EN OPNIEUW LATEN AANMAKEN, NIET ALLEEN needsUpdate (2026-08-26).
  // sizeBgCanvas geeft het bron-canvas een andere AFMETING. Alleen needsUpdate
  // zetten laat three de nieuwe inhoud in de bestaande GPU-texture kopiëren, en
  // die heeft nog de oude maat. Chrome weigert dat kopieerverzoek dan:
  //
  //   GL_INVALID_VALUE: glCopySubTextureCHROMIUM: Offset overflows texture
  //   dimensions.
  //
  // De upload mislukt, dus in het deel dat niet overschreven werd blijft de
  // OUDE texture staan. Precies wat Vince zag: hij sleepte van 819 naar 731, de
  // achtergrond werd bijna overal vlak, maar rechts bleef een strook met de oude
  // lijnen hangen. Zes van die fouten stonden in zijn console; ik kon ze in mijn
  // eigen testbrowser niet zien, want daar laadt de scene nooit af.
  //
  // dispose() dwingt three om bij het volgende gebruik een nieuwe texture van de
  // juiste maat te alloceren. Draait alleen bij een echte maatwijziging: de
  // guard bovenin deze functie keert eerder terug als w en h gelijk zijn.
  bgTexture.dispose();
  bgTexture.needsUpdate = true;
  measureHeroTxtBot();         // tekst-onderkant verschuift bij resize/oriëntatie → model-anchor bijwerken
}
applyViewportSize();
window.addEventListener('resize', applyViewportSize);
window.addEventListener('orientationchange', applyViewportSize);

// Typografie: fade + translate per sectie zodra hij in beeld scrollt. Slechts 1 sectie
// tegelijk zichtbaar (Vince: nooit een moment waarop je twee secties tegelijk in de
// linkerkolom ziet, bv. Hero+Engineering of Research+volgende) — de oude versie
// togglede elk .content-element onafhankelijk op dezelfde vaste drempel (0.4), waardoor
// twee aangrenzende secties rond de overgang allebei ≥40% zichtbaar konden zijn en dus
// allebei tegelijk .visible kregen. Nu kiest elke observer-tick de sectie met de HOOGSTE
// intersection-ratio uit de hele set en verbergt de rest — altijd hooguit één winnaar.
// Fijnmazige thresholds (i.p.v. één vaste 0.4) zodat de ratio's precies genoeg zijn om
// eerlijk te vergelijken.
const _contentEls = Array.from(document.querySelectorAll('.content'));
const _contentRatios = new Map(_contentEls.map(el => [el, 0]));
const contentObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => _contentRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
  let best = null, bestRatio = 0;
  _contentRatios.forEach((ratio, el) => { if (ratio > bestRatio) { bestRatio = ratio; best = el; } });
  _contentEls.forEach(el => {
    const aan = (el === best && bestRatio >= 0.4);
    el.classList.toggle('visible', aan);
    // Blijvend merkteken zodra een blok een keer getoond is. De CSS gebruikt het om
    // de uitgang te scheiden van de allereerste entree: rijzen bij binnenkomst,
    // alleen faden bij vertrek.
    if (aan) el.classList.add('seen');
  });
}, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }); // 0, 0.05, ..., 1.0
_contentEls.forEach(el => contentObserver.observe(el));

// ───────── Opname-modus (?capture=<seconden>, default 12) ─────────
// Voor de mobiele video-route: speelt de animatie deterministisch 0→1 af (via _capProgress)
// en neemt de WebGL-canvas op met MediaRecorder → download als spool-capture.webm. Alleen
// actief met ?capture in de URL; geen effect op normaal gebruik. Draaien op een desktop-GPU
// (headless rendert de 3D niet). Render de canvas op volle kwaliteit, dus de video = wat je
// op desktop ziet.
(() => {
  const capParam = new URLSearchParams(location.search).get('capture');
  if (capParam === null) return;
  const secs = Math.max(2, parseFloat(capParam) || 12);
  const ov = document.createElement('div');
  ov.style.cssText = 'position:fixed;top:12px;left:12px;z-index:99999;background:rgba(0,0,0,.65);'
    + 'color:#CFEEDB;font:600 14px/1.4 monospace;padding:10px 14px;border-radius:6px;white-space:pre;';
  ov.textContent = 'Opname klaarzetten…';
  (document.body || document.documentElement).appendChild(ov);

  const ready = () => canvas.classList.contains('ready');
  const waitReady = () => ready() ? setTimeout(start, 500) : setTimeout(waitReady, 120);
  setTimeout(waitReady, 700);

  function start() {
    let stream;
    try { stream = canvas.captureStream(60); }
    catch (e) { ov.textContent = 'captureStream faalt: ' + e.message; return; }
    const pick = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
      .find(m => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || 'video/webm';
    const rec = new MediaRecorder(stream, { mimeType: pick, videoBitsPerSecond: 16000000 });
    const chunks = [];
    rec.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      _capProgress = null;
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      // GEEN auto-download (Chrome blokkeert die stil) — toon een knop die je zelf klikt.
      ov.style.cssText = 'position:fixed;inset:auto 12px 12px 12px;z-index:99999;'
        + 'background:rgba(0,0,0,.8);padding:18px 20px;border-radius:10px;text-align:center;';
      ov.innerHTML = '';
      const a = document.createElement('a');
      a.href = url; a.download = 'spool-capture.webm';
      a.textContent = '⬇ Klik om te downloaden — spool-capture.webm (' + (blob.size / 1048576).toFixed(1) + ' MB)';
      a.style.cssText = 'display:inline-block;color:#1B2A1F;background:#63D992;font:700 16px/1.3 monospace;'
        + 'padding:14px 20px;border-radius:8px;text-decoration:none;';
      ov.appendChild(a);
    };
    rec.start();
    const t0 = performance.now();
    (function ramp(now) {
      const t = Math.min(1, ((now || performance.now()) - t0) / (secs * 1000));
      _capProgress = t;
      ov.textContent = 'Opnemen… ' + Math.round(t * 100) + '%  (' + secs + 's, ' + pick.split(';')[0] + ')';
      if (t < 1) requestAnimationFrame(ramp);
      else setTimeout(() => rec.stop(), 400);
    })(t0);
  }
})();

// Tier-row — drag-to-scroll + prev/next arrows + disabled-state op de randen
(function setupTierRow() {
  const row = document.querySelector('.tier-row-scroll');
  if (!row) return;
  const prev = document.querySelector('[data-tier-prev]');
  const next = document.querySelector('[data-tier-next]');

  function updateArrows() {
    if (!prev || !next) return;
    const max = row.scrollWidth - row.clientWidth;
    prev.disabled = row.scrollLeft <= 4;
    next.disabled = row.scrollLeft >= max - 4;
  }
  updateArrows();
  row.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows);

  function stepDist() {
    const card = row.querySelector('.tier-card');
    if (!card) return row.clientWidth * 0.8;
    const styles = getComputedStyle(row);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }
  prev && prev.addEventListener('click', () => {
    row.scrollBy({ left: -stepDist(), behavior: 'smooth' });
  });
  next && next.addEventListener('click', () => {
    row.scrollBy({ left: stepDist(), behavior: 'smooth' });
  });

  // Drag-to-scroll met pointer events — laat link-clicks intact (small-move check)
  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  let moved = 0;
  row.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging = true;
    moved = 0;
    startX = e.clientX;
    startScroll = row.scrollLeft;
    row.classList.add('is-dragging');
    row.setPointerCapture(e.pointerId);
  });
  row.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    moved = Math.max(moved, Math.abs(dx));
    row.scrollLeft = startScroll - dx;
  });
  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    row.classList.remove('is-dragging');
    try { row.releasePointerCapture(e.pointerId); } catch (_) {}
  }
  row.addEventListener('pointerup', endDrag);
  row.addEventListener('pointercancel', endDrag);
  // Onderdruk link-clicks tijdens een drag (>4px), laat tap-clicks door
  row.addEventListener('click', (e) => {
    if (moved > 4) { e.preventDefault(); e.stopPropagation(); }
  }, true);
})();

// "Browse filaments"-CTA in de Products-step: glide naar de START van de
// producten-sectie (parking-band top), niet door tot de kaarten (#tier-row). Zo
// komt de sectie-header bovenaan in beeld met de kaarten eronder.
document.querySelectorAll('[data-scroll-to-cards]').forEach((el) => {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    lenis.scrollTo(parkingTopY(), {
      duration: 1.0,
      easing: (t) => 1 - (1 - t) * (1 - t), // easeOutQuad, gelijk aan de snap-glide
    });
  });
});

// Custom scroll indicator — edge-peeking pill, slides + breathes on scroll.
// .on-dark = mark Paper (op dark journey); zonder die class = mark Pine (op Paper parking).
(function setupScrollRail() {
  const rail = document.getElementById('scroll-rail');
  const mark = document.getElementById('scroll-mark');
  if (!rail || !mark) return;

  let ticking = false;
  let idleTimer = null;

  function tick() {
    ticking = false;
    const docHeight = scrollMax();
    if (docHeight <= 0) { rail.style.display = 'none'; return; }
    rail.style.display = '';
    const pct = Math.max(0, Math.min(1, getScrollY() / docHeight));
    const railH = rail.offsetHeight;
    const markH = mark.offsetHeight;
    const markTop = pct * (railH - markH);
    // Ook licht boven een donkere band of de footer, net als scroll-rail.js op de andere
    // pagina's; anders verdween de Pine-mark op de Pine-footer (Vince 2026-10-13). De
    // rects worden gelezen vóór de schrijfactie hieronder, dus dit kost geen extra layout.
    const markY = rail.getBoundingClientRect().top + markTop + markH / 2;
    const overDark = Array.prototype.some.call(document.querySelectorAll('.band--dark, .site-footer'), (el) => {
      const r = el.getBoundingClientRect();
      return markY >= r.top && markY < r.bottom;
    });
    mark.style.top = markTop + 'px';

    // .on-dark zolang de canvas-bg nog donker is. Threshold via scrollProgress
    // matched de bg-lerp-curve (Paper bereikt rond p=0.95).
    rail.classList.toggle('on-dark', scrollProgress < 0.95 || overDark);
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(tick);
      ticking = true;
    }
    mark.classList.add('is-scrolling');
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => mark.classList.remove('is-scrolling'), 520);
  }

  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', tick, { passive: true });
  setTimeout(tick, 50);
  setTimeout(tick, 400);

  // Drag-to-scroll
  let dragging = false;
  let dragStartClientY = 0;
  let dragStartScrollY = 0;

  function startDrag(clientY) {
    dragging = true;
    dragStartClientY = clientY;
    dragStartScrollY = getScrollY();
    mark.classList.add('is-dragging');
    document.body.style.userSelect = 'none';
    document.documentElement.style.scrollBehavior = 'auto';
  }
  function moveDrag(clientY) {
    if (!dragging) return;
    const deltaY = clientY - dragStartClientY;
    const railH = rail.offsetHeight;
    const markH = mark.offsetHeight;
    const docHeight = scrollMax();
    const trackable = railH - markH;
    if (trackable <= 0 || docHeight <= 0) return;
    const scrollDelta = deltaY * (docHeight / trackable);
    const newScroll = Math.max(0, Math.min(docHeight, dragStartScrollY + scrollDelta));
    mark.style.top = ((newScroll / docHeight) * trackable) + 'px';
    // immediate: zet Lenis' interne target direct (geen lerp), zodat de mark
    // 1:1 met de muis meebeweegt i.p.v. dat Lenis ertegenin lerpt.
    lenis.scrollTo(newScroll, { immediate: true });
  }
  function endDrag() {
    if (!dragging) return;
    dragging = false;
    mark.classList.remove('is-dragging');
    document.body.style.userSelect = '';
    document.documentElement.style.scrollBehavior = '';
  }

  mark.addEventListener('mouseenter', () => mark.classList.add('is-hovering'));
  mark.addEventListener('mouseleave', () => mark.classList.remove('is-hovering'));
  mark.addEventListener('mousedown', (e) => { startDrag(e.clientY); e.preventDefault(); });
  mark.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) startDrag(e.touches[0].clientY);
  }, { passive: true });
  window.addEventListener('mousemove', (e) => moveDrag(e.clientY));
  window.addEventListener('touchmove', (e) => {
    if (dragging && e.touches.length === 1) {
      moveDrag(e.touches[0].clientY);
      e.preventDefault();
    }
  }, { passive: false });
  window.addEventListener('mouseup', endDrag);
  window.addEventListener('touchend', endDrag);
  window.addEventListener('touchcancel', endDrag);
})();

// === Section-snap voor de 3D-journey ===
// Anchors: Hero(0) → Engineering(1) → Research(2) → Products(3) → Catalogus-top(4).
// Die vallen samen met de keyframe-stops (scrollProgress 0/0.25/0.5/0.75/1.0), dus
// elke snap landt precies op een reveal. In de journey (scrollY < parking-top)
// onderschept snap wheel/arrows/swipe en gleidt één sectie verder of terug. In de
// catalogus laten we de scroll los — pas boven de parking-grens pakt snap het over.
//
// Momentum-proof lock: een trackpad-flick stuurt geen één event maar een lange
// staart van momentum-events die seconden kan doorrollen. De lock gaat daarom pas
// weer open als (a) de glide klaar is én (b) er een stille periode zonder wheel-
// events is geweest. Een vaste cooldown was te kort: de na-rollende momentum tikte
// dan een tweede snap aan ("wacht even, scrolt dan vanzelf door"). Keyboard/touch
// hebben geen momentum-staart, dus die komen direct na de glide weer vrij.
// Section-snap verwijderd (companion-redesign inc 1): scroll is nu native (Lenis
// smooth zonder snap). De 3D-animatie speelt scroll-gekoppeld mee zonder de scroll
// te onderscheppen; geen skip-knop meer nodig.

